/**
 * asrSession — EVAS2.0 檢查工作站的 KKASR session 狀態機
 *
 * 這個 module 只管「跟 KKASR 之間的一場收音」：建 session、cursor 輪詢逐字稿
 * 與語音指令、EOS、以及把指令事件換算成配速員的計時點。
 *
 * ── 三件事刻意不做在這裡 ──
 *   1. 音訊上行（WebSocket / 麥克風 / 音檔）：那是瀏覽器裝置資源，生命週期
 *      綁在元件上，放進 store 只會讓「誰該關掉它」變得沒人說得清。由
 *      Exam2DetailPage 持有，這裡只保管 sessionId 與狀態。
 *   2. 截圖：走既有的 examinations / image API，與本 module 無關。
 *   3. LLM 最終結果：EOS 之後 KKASR 走 webhook 推回 kingcolon server 落地
 *      examination_transcripts。前端不負責保存，報告工作站讀 DB。
 *      這裡只輪詢它「跑完了沒」，用來把狀態列推到「報告已產生」。
 *      Key Info 面板不吃 LLM——它吃每句 refine 自帶的 keywords（見下）。
 *
 * ── cursor，不是 pop ──
 *   逐字稿與指令一律帶 after_seq 讀（KkasrApi.refineSince / commandsSince）。
 *   不帶 after_seq 會走 KKASR 的舊 pop 模式：讀過即消失，瀏覽器一重整那段
 *   逐字稿就永久遺失。
 */

import Vue from 'vue'
import { KkasrApi, resolveKkasrEndpoints } from '@/services/kkasr'
import { voiceCommandCues } from '@/services/kkasr/voice_command_cues'
import { evas2PacerServices, evas2ReportServices, isRetryableRequestError } from '@/services/evas2'
import { photoEditWindows, photoEditWindowRange } from '@/utils/dictationFields'
import {
  classifyEvas2ReportAggregate,
  EVAS2_REPORT_AGGREGATE_STATUS,
  EVAS2_REPORT_WAIT_TIMEOUT_MS,
} from '@/utils/evas2ReportAggregate'
import {
  loadSessionOverrides,
  saveSessionOverrides,
  pruneSessionOverrides,
  loadSessionReloadResume,
  clearSessionReloadResume,
  markFinalAuthRecovery,
  inspectFinalAuthRecovery,
  clearFinalAuthRecovery,
} from '@/utils/asrSessionOverrides'

const NO_TIME = '00:00:00'
const PACER_CLOCK_RE = /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/
const PACER_DURATION_RE = /^\d{2}:[0-5]\d:[0-5]\d$/
const PACER_TIMING_PHASES = new Set([
  'listening',
  'inserting',
  'proximal_withdrawal',
  'withdrawing',
  'finished',
])
const PACER_TIMING_CLOCK_FIELDS = [
  'time_start',
  'time_insertion_level',
  'time_landmark_splenic_flexure',
  'time_finished',
]
const PACER_TIMING_DURATION_FIELDS = [
  'insertionTime',
  'proximalColonWithTime',
  'withdrawTime',
  'totalExamTime',
]

export const POLL_DRAFT_MS = 250
export const FINISH_GRACE_MS = 5000
const POLL_TRANSCRIPT_MS = 1000
const POLL_LLM_MS = 2000
// EOS 之後 ASR 還會吐最後幾句 refine，寬限期內繼續輪詢逐字稿
const EOS_GRACE_MS = 60000
const PROCEDURE_CONTEXT_AUTH_ERROR = '登入已失效，尚未建立報告前置條件；本次收音未開始，請重新登入後再試'
const FINAL_AUTH_ERROR = '登入已失效；請重新登入並重整此病例，以取回本場既有結果'

const isAuthHttpResponse = (response) => (
  [401, 403].includes(Number(response && response.status))
)

/** exact-session report／extract只讀結果分類；failed允許保留原HTTP failure事實。 */
function classifyLlmReadback(response, sessionId) {
  if (isAuthHttpResponse(response)) return { status: 'auth' }
  const body = response && response.body
  if (!body || String(body.session_id || '') !== String(sessionId || '')) {
    return { status: 'invalid', httpStatus: Number(response && response.status) || 0 }
  }
  if (body.status === 'failed') return { status: 'failed', body }
  if (!response.ok) {
    return { status: 'invalid', httpStatus: Number(response.status) || 0 }
  }
  if (body.status === 'succeeded') return { status: 'succeeded', body }
  if (body.status === 'pending') return { status: 'pending', body }
  return { status: 'invalid', httpStatus: Number(response.status) || 0 }
}

/** 詞表／圖鑑目錄載入的重試次數與退避基數（指數退避：0.5s → 1s → 2s） */
const TERMS_RETRY_TIMES = 3
const TERMS_RETRY_BASE_MS = 500
/** 三次都失敗之後，靠輪詢隔多久再試一輪（A11 要求恢復後 60 秒內補載） */
const TERMS_RETRY_INTERVAL_MS = 10000
const KEY_INFO_UI_SCHEMA = 'evas2-key-info-ui/v1'
const KEY_INFO_UI_SOURCES = new Set(['refine_low_confidence', 'final_transcript'])
const FAST_CORRECTION_SCHEMA = 'evas2-fast-correction/v1'
const FAST_CORRECTION_PROFILE = 'gemma4-31b-q4km-committed-refine-fast-v1'

export function normalizeKeyInfoUi(value, expectedSource) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  if (
    value.schema !== KEY_INFO_UI_SCHEMA
    || value.source !== expectedSource
    || !KEY_INFO_UI_SOURCES.has(value.source)
    || value.review_status !== 'unreviewed'
    || !Array.isArray(value.terms)
    || value.terms.some((term) => typeof term !== 'string')
  ) return null
  return {
    schema: KEY_INFO_UI_SCHEMA,
    source: value.source,
    review_status: 'unreviewed',
    terms: value.terms.slice(),
  }
}

export function normalizeFastCorrection(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const allowedKeys = new Set([
    'schema', 'status', 'profile', 'model', 'thinking_enabled', 'review_status',
    'source_sha256', 'source_seq', 'text', 'display_text', 'applied', 'reason',
    'request_sha256', 'elapsed_sec',
  ])
  if (Object.keys(value).some((key) => !allowedKeys.has(key))) return null
  const status = value.status
  const textValid = status === 'succeeded'
    ? typeof value.text === 'string' && Boolean(value.text.trim())
    : value.text == null
  const displayTextValid = status === 'succeeded'
    ? typeof value.display_text === 'string' && Boolean(value.display_text.trim())
    : value.display_text == null
  if (
    value.schema !== FAST_CORRECTION_SCHEMA
    || value.profile !== FAST_CORRECTION_PROFILE
    || !['pending', 'succeeded', 'failed'].includes(status)
    || value.thinking_enabled !== false
    || value.review_status !== 'unreviewed'
    || typeof value.model !== 'string'
    || !/^[a-f0-9]{64}$/.test(String(value.source_sha256 || ''))
    || !Number.isInteger(value.source_seq)
    || value.source_seq <= 0
    || typeof value.applied !== 'boolean'
    || typeof value.reason !== 'string'
    || !textValid
    || !displayTextValid
  ) return null
  return {
    schema: FAST_CORRECTION_SCHEMA,
    status,
    profile: FAST_CORRECTION_PROFILE,
    model: value.model,
    thinking_enabled: false,
    review_status: 'unreviewed',
    source_sha256: value.source_sha256,
    source_seq: value.source_seq,
    text: status === 'succeeded' ? value.text : null,
    display_text: status === 'succeeded' ? value.display_text : null,
    applied: value.applied,
    reason: value.reason,
    request_sha256: typeof value.request_sha256 === 'string' ? value.request_sha256 : null,
    elapsed_sec: Number.isFinite(value.elapsed_sec) ? value.elapsed_sec : null,
  }
}

/**
 * 從 append-only REFINE ledger 投影目前畫面版本。
 *
 * 只有同 session、完全相同的有效音訊區間，且 publication seq 嚴格增加時才視為
 * 同一句的新版本。無時間軸或不精確相同的資料維持 append，避免猜測兩段音訊相同。
 */
export function currentRefineLineProjection(lines) {
  const projected = []
  const latestByRange = new Map()
  for (const line of Array.isArray(lines) ? lines : []) {
    const sessionId = String(line && line.session_id || '')
    const start = line && line.start
    const end = line && line.end
    const seq = line && line.seq
    const hasIdentity = (
      sessionId
      && line.start != null
      && line.end != null
      && Number.isFinite(start)
      && Number.isFinite(end)
      && end > start
      && line.seq != null
      && Number.isInteger(seq)
      && seq > 0
    )
    if (!hasIdentity) {
      projected.push(line)
      continue
    }
    const identity = JSON.stringify([sessionId, start, end])
    const previous = latestByRange.get(identity)
    if (previous && seq > previous.seq) {
      projected[previous.index] = line
      latestByRange.set(identity, { index: previous.index, seq })
      continue
    }
    projected.push(line)
    if (!previous) latestByRange.set(identity, { index: projected.length - 1, seq })
  }
  return projected
}

/**
 * 同一次 cursor 回傳可能包含 raw、pending 與 succeeded。若直接一次 commit，Vue 只會
 * render 同 range 的最新版，醫師從未看見「先 ASR、後校稿」。只有本批首次出現的
 * raw 需要切一個 Vue render boundary；先前 poll 已經發布過的來源不重播、不閃回。
 */
async function appendRefineItemsWithRawRender({
  state: s,
  commit,
  items,
  nextAfterSeq,
  truncated,
  isStale,
}) {
  const incoming = Array.isArray(items) ? items : []
  const priorSeqs = new Set(s.lines.map((line) => Number(line && line.seq)))
  let pending = []
  let bufferedNewRawSeqs = new Set()

  for (const item of incoming) {
    const correction = normalizeFastCorrection(item && item.fast_correction)
    if (correction && bufferedNewRawSeqs.has(correction.source_seq)) {
      commit('appendLines', { items: pending })
      pending = []
      bufferedNewRawSeqs = new Set()
      await Vue.nextTick()
      if (isStale()) return false
    }
    pending.push(item)
    const seq = Number(item && item.seq)
    if (!correction && Number.isInteger(seq) && seq > 0 && !priorSeqs.has(seq)) {
      bufferedNewRawSeqs.add(seq)
    }
  }

  commit('appendLines', { items: pending, nextAfterSeq, truncated })
  return true
}

/**
 * 配速 milestone 的重送上限與退避基數。
 *
 * 重送掛在每秒一次的 pollOnce 上，沒有上限也沒有退避時，一個永遠不會成功的
 * 請求會每秒重打一次直到醫師離開頁面（線上實測：同一筆 409 連打 25 分鐘、
 * 1536 次）。4xx 由 isRetryableRequestError 直接判定不重送，這組參數只服務
 * 「暫時性故障」——瞬斷與 5xx 值得重送，但不能無止盡。
 *
 * ⚠️ 實際節奏是「第 1 次失敗後等 2 秒重送、第 2 次失敗後等 4 秒重送，
 *    第 3 次失敗就放棄」——因為 recordPacerRetryFailure 是在 attempts 累加到
 *    PACER_RETRY_TIMES 時就出佇列，不會再排第 3 次退避。從第一次失敗到放棄
 *    約 6 秒收斂。這裡刻意寫實際行為而不是「2s→4s→8s 共 3 次」的直覺推算：
 *    先前註解與 ADR 都寫成後者，與 code 對不上。
 */
const PACER_RETRY_TIMES = 3
const PACER_RETRY_BASE_MS = 2000

/**
 * 配速指令 → examinations 的時間欄位。
 *
 * 只服務「這個里程碑填了什麼」這一個判斷（見 setPacerTiming 對
 * pacerDroppedCommands 的收斂）。真正的寫入與驗證都在 server。
 *
 * ⚠️ 與 server/service/evas2/pacer-timing.js 的 COMMAND_POINT 是同一張表，
 *    改一邊必須改另一邊——沒有任何機制會替你發現兩邊分岔。
 */
const PACER_POINT_FIELDS = {
  evas_go: 'time_start',
  insertion_to: 'time_insertion_level',
  proximal_time: 'time_landmark_splenic_flexure',
  evas_finish: 'time_finished',
}

/** 四個時間欄位裡，'00:00:00'／null／'' 一律代表「還沒記錄」（與 server 的 isUnset 同一套）。 */
function hasPoint(value) {
  return !!value && value !== NO_TIME
}

/** API 的英文內部訊息不可直接丟給診間；失敗一律明示「尚未儲存」與可採動作。 */
function pacerCorrectionWarning(err) {
  const status = Number(err && err.status)
  const message = String((err && err.message) || '')
  if (message.includes('缺少 result') || message.includes('回應不完整')) {
    return '配速員修正尚未儲存：伺服器回應不完整，請稍後再試一次'
  }
  if (status === 401 || status === 403) {
    return '配速員修正尚未儲存：登入狀態已失效，請重新登入後再試一次'
  }
  if (status === 404) {
    return '配速員修正尚未儲存：找不到這筆檢查，請重新整理頁面後再試一次'
  }
  if (status === 409) {
    return '配速員修正尚未儲存：時間先後順序不正確，請重新確認四個時間點後再試一次'
  }
  if (status >= 400 && status < 500) {
    return '配速員修正尚未儲存：輸入內容無法接受，請確認時間格式與欄位後再試一次'
  }
  if (status >= 500) {
    return '配速員修正尚未儲存：伺服器暫時無法處理，請稍後再試一次'
  }
  return '配速員修正尚未儲存：網路連線中斷，請確認連線後再試一次'
}

function normalizePacerCorrectionIdentity(identity) {
  const examinationId = identity && identity.examinationId
  const routeToken = identity && identity.routeToken
  if (examinationId == null || routeToken == null || String(routeToken) === '') return null
  return {
    examinationId: String(examinationId),
    routeToken: String(routeToken),
  }
}

function samePacerCorrectionIdentity(expected, current) {
  return !!expected
    && !!current
    && current.examinationId === expected.examinationId
    && current.routeToken === expected.routeToken
}

/**
 * correction 的 2xx 是一次 publication，不是「有 result 就算成功」。四個正本時間、
 * insertion position、phase 與四個衍生值必須同批齊全；缺欄或錯型一律保留舊快照。
 * time_* 接受 null 以相容舊列，非 null 則必須是 canonical HH:mm:ss。
 */
function isCompletePacerTimingSnapshot(timing) {
  if (!timing || typeof timing !== 'object' || Array.isArray(timing)) return false
  const hasOwn = (field) => Object.prototype.hasOwnProperty.call(timing, field)
  if (!hasOwn('phase') || !PACER_TIMING_PHASES.has(timing.phase)) return false
  if (!hasOwn('insertionLevel')) return false
  if (!(timing.insertionLevel == null || typeof timing.insertionLevel === 'string')) return false
  if (!PACER_TIMING_CLOCK_FIELDS.every((field) => (
    hasOwn(field)
    && (timing[field] == null || PACER_CLOCK_RE.test(timing[field]))
  ))) return false
  return PACER_TIMING_DURATION_FIELDS.every((field) => (
    hasOwn(field) && PACER_DURATION_RE.test(timing[field])
  ))
}

/**
 * 終局提示的兩種語義。**兩者的「已經解決」定義是相反的**，所以必須分流：
 *   - missing：里程碑是空的（4xx 被拒、重送放棄），被填上就算解決。
 *   - wrongValue：里程碑有值、但值是錯的（first-wins 誤觸），只有值被改掉才算解決；
 *     insertion_to 還要連同位置一起核對。
 * 混用一條規則的代價：wrongValue 的欄位依定義必然有值，用 missing 的規則收斂，
 * 下一個成功的里程碑／F5 hydrate 就會把提示濾掉，病歷留著誤觸時刻、畫面不留痕跡。
 */
const PACER_DROPPED_MISSING = 'missing'
const PACER_DROPPED_WRONG_VALUE = 'wrongValue'

/**
 * 這筆終局提示是否已經被解決（可以從畫面上收掉）了。
 * 收斂規則只寫在這裡一處，setPacerTiming 的三個入口（milestone 成功、correction、
 * F5 hydrate）共用它。
 */
function isPacerDroppedResolved(item, timing) {
  const point = timing[PACER_POINT_FIELDS[item.command]]
  if (
    item.command === 'insertion_to'
    && Object.prototype.hasOwnProperty.call(item, 'expectedPoint')
    && Object.prototype.hasOwnProperty.call(item, 'expectedInsertionLevel')
  ) {
    return point === item.expectedPoint
      && timing.insertionLevel === item.expectedInsertionLevel
  }
  if (item.kind === PACER_DROPPED_WRONG_VALUE) {
    return point !== item.point
      || (
        Object.prototype.hasOwnProperty.call(item, 'insertionLevel')
        && timing.insertionLevel !== item.insertionLevel
      )
  }
  return hasPoint(point)
}

/**
 * 把一筆終局提示併進清單：同一個 command 只留最新一筆，並照配速步驟排序。
 * Vuex 的 mutation 不能互相呼叫，recordPacerDroppedCommand 與 recordPacerRetryFailure
 * 的放棄分支共用這支純函式，避免其中一處改了 entry 形狀、另一處沒跟上。
 */
function upsertPacerDropped(list, entry) {
  return list
    .filter((item) => item.command !== entry.command)
    .concat(entry)
    .sort((a, b) => PACER_STEPS[a.command] - PACER_STEPS[b.command])
}

/**
 * evas_finish 被階段擋下時要顯示的話。
 *
 * 只寫「目前階段為 inserting」等於把系統內部名詞丟給醫師：他既不知道那是什麼，
 * 也不知道下一步該做什麼。這裡每一句都必須是**可執行的動作**。
 */
const FINISH_BLOCKED_HINTS = {
  listening: '尚未開始檢查（沒有 EVAS GO 記錄），無法結束。請先用配速員的「修改」補上開始時間。',
  inserting: '尚未記錄 Proximal Time，無法結束檢查。請先用配速員的「修改」補上再結束。',
  proximal_withdrawal: '尚未記錄 Proximal Time，無法結束檢查。請先用配速員的「修改」補上再結束。',
  finished: '這次檢查已經結束過了，不會重複記錄結束時間。',
}

/**
 * 配速員四個正式語音事件。數字只供舊 PacerPanel 的 step prop 相容使用；
 * 真正狀態由 server 回傳的 phase 決定。
 */
export const PACER_STEPS = {
  evas_go: 1,
  insertion_to: 2,
  proximal_time: 3,
  evas_finish: 4,
}

const PACER_PHASE_STEPS = {
  listening: 0,
  inserting: 1,
  proximal_withdrawal: 2,
  withdrawing: 3,
  finished: 4,
}

/**
 * Cleansing Level 語音指令 → examinations.cleanScale 的值。
 *
 * 值是 store/data/input-list 的 cleanScaleList id（'1'=Excellent … '4'=Poor），
 * 與 CleansingLevelPanel 手動點選、舊版檢查介面、國健署上傳同一組代碼。
 *
 * ⚠️ **last-wins**，與配速員里程碑的 first-wins 相反：清潔度是一個可以改口的
 *    判斷（醫師沖洗完會重講），最後講的那次才算數。即時 transport 可能重送，
 *    但同一序列順序固定，因此重送與只收到最後一個的結果相同。
 */
export const CLEANSING_LEVEL_VALUES = {
  cleansing_excellent: '1',
  cleansing_good: '2',
  cleansing_fair: '3',
  cleansing_poor: '4',
}

/**
 * 病理圖鑑呼叫指令 → 「顯示哪一本圖鑑」。
 *
 * 這裡只認 command 字串；標題與圖片路徑由 KKASR 的 /atlas 供應（見 atlasCatalog），
 * 所以**新增一本圖鑑不必動這個檔案**——後端加完，前端自動認得。
 *
 * ⚠️ **last-wins**，與配速員里程碑的 first-wins 相反：醫師會來回對照 JNET 與
 *    Kudo，最後講的那本才是現在要看的。指令事件順序固定，同頁 transport 重送
 *    仍維持 last-wins；F5 不重播歷史 command side effect。
 *
 * ⚠️ atlas_clear（"show off" / 「關閉圖鑑」）不在目錄裡，消費端視為清空。
 */
export const ATLAS_CLEAR_COMMAND = 'atlas_clear'

const isAtlasCommand = (command) => typeof command === 'string' && command.startsWith('atlas_')

// 輪詢 timer 放模組層而不是 state：Vuex state 應該是可序列化的資料，
// 塞 timer id 進去在 devtools 時光旅行時會變成殭屍計時器。
let transcriptTimer = null
let draftTimer = null
let llmTimer = null
let graceTimer = null

// 上一次 pollOnce 還沒跑完。每秒一次的 setInterval 配上兩次 await，KKASR 慢回應
// 時下一 tick 會用**還沒推進的 cursor** 再撈一次，同一段逐字稿與 Key Info 就
// 重複出現在畫面上。旗標放模組層，理由同上面的 timer。
let pollInFlight = false
// FAST 是單格 snapshot，不碰 cursor，可用獨立低延遲輪詢；仍要防慢回應堆疊。
let draftPollInFlight = false
// 詞表／圖鑑目錄的長週期重試：自己的計時器（不搭輪詢便車，理由見 retryTermsIfNeeded）
let termsTimer = null
let termsRetryInFlight = false
// command cursor 先前進、server 寫入後完成；中間若瞬斷，以獨立 queue 重送。
let pacerFlushInFlight = false
// LLM 輪詢（每 2 秒）同樣不可堆疊。EOS 之後這條會與逐字稿寬限期的輪詢**並存 60 秒**，
// 兩條打同一個 origin；瀏覽器對同 host 的併發連線有上限（HTTP/1.1 是 6），
// 這裡塞滿的話，那幾句 EOS 之後才吐出來的 refine（EOS_GRACE_MS 存在的理由）會收不到。
let llmPollInFlight = false
// store 的「第幾代」。teardown 會推進它，讓**離開頁面之前發出、離開之後才回來**的
// 請求認得出自己已經過期——否則那些遲到的失敗會在已經拆掉的 store 上重開計時器。
let storeEpoch = 0

function clearInterval_(id) {
  if (id) window.clearInterval(id)
  return null
}

function clearTimeout_(id) {
  if (id) window.clearTimeout(id)
  return null
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

/** ms 時間戳 → 'HH:mm:ss'（DB 的 time 欄格式，與舊版 moment().format 相同） */
export function msToHHmmss(ms) {
  const d = new Date(ms)
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

/** 兩個 'HH:mm:ss' 相減 → 'HH:mm:ss'（負數與缺值一律回 00:00:00，與舊版一致） */
export function diffHHmmss(later, earlier) {
  if (!later || !earlier) return NO_TIME
  const toSec = (t) => {
    const p = String(t).split(':')
    return Number(p[0]) * 3600 + Number(p[1]) * 60 + Number(p[2])
  }
  let c = toSec(later) - toSec(earlier)
  if (!(c > 0)) return NO_TIME
  const h = Math.floor(c / 3600)
  const i = Math.floor((c - h * 3600) / 60)
  const s = c - h * 3600 - i * 60
  return `${pad2(h)}:${pad2(i)}:${pad2(s)}`
}

const initialState = () => ({
  httpUrl: '',
  wsUrl: '',

  sourceMode: 'mic', // 'mic' | 'file'
  sessionId: null,
  examinationId: null,
  // idle → starting → recording → finalizing → done
  //                 ↘ conflict / error       ↘ aborted
  status: 'idle',
  error: null,
  // 409：別間檢查還在收音。要接管得使用者明確再按一次（force）
  conflictSession: null,

  // session 起點的 wall clock（ms）。指令事件的 at_sec 是「相對 session 起點
  // 的秒數」，要寫進 examinations 的 time 欄位必須換算回真實時刻。
  startedAtMs: null,

  // --- 逐字稿（cursor）---
  lines: [], // { seq, text, start, end }
  // EOS 後完整音訊重解的權威 AI revision；raw lines 保留供指令／照片口述回溯。
  finalTranscript: null,
  // FAST 只有一格 snapshot，永不 append 到 lines，也不進 DB / dictation / LLM。
  draft: null,
  refineCursor: 0,
  truncated: false,

  // --- 語音指令（cursor）---
  commands: [], // { seq, command, at_sec, end_sec, detected_at_sec }
  commandCursor: 0,
  // 同一 session 是否已有 KKASR 接受的 EVAS GO。F5 不把歷史 commands 放回
  // consumer，另留這個無副作用狀態才能在 current transport ready 後恢復第三態。
  hasAcceptedGo: false,

  // 畫面可顯示 EVAS GO 前的 draft 供操作確認；正式 LLM 來源由 KKASR 優先取
  // EVAS GO → EVAS finish，沒有有效 GO 時則回退整場 final。

  // 已由 server 接受的 command 記號只供診斷；first-wins 正本在 DB transaction。
  pacerApplied: {},

  // examinations DB 是正本；這份是 server API 回傳的完整快照，供狀態機與 UI 使用。
  pacerTiming: { phase: 'listening' },
  /**
   * 暫時性警示。會自癒的那一種：載入失敗、可重送的失敗、階段還沒到。
   * 下一個成功的指令就該把它清掉，所以**不能**拿來承載終局失敗（見下）。
   */
  pacerWarning: null,
  /**
   * 終局失敗的里程碑：4xx 被 server 拒收、或 first-wins 讓醫師剛剛講的時刻沒寫進去。
   *
   * 刻意與 pacerWarning 分開一欄。原本兩者共用 pacerWarning，於是
   * 「insertion_to 吃 400 → 請醫師手動補上」的訊息，會被幾十秒後成功的
   * proximal_time（或 F5 的 hydrate）以 setPacerWarning(null) 洗掉，
   * 醫師還沒看到提示，那筆臨床時刻就永久消失且畫面上不留任何痕跡。
   * 一欄一語義：這一欄只有「該里程碑真的被填上」或「醫師自己關掉」才會清。
   * 形狀：[{ command, message, atMs }]，同一 command 只留最新一筆。
   */
  pacerDroppedCommands: [],
  pendingPacerEvents: [],
  // command → { attempts, nextAttemptAtMs }。刻意與 pendingPacerEvents 分開：
  // 佇列說的是「還有哪一筆沒送出去」，這裡說的是「已經替它重試幾次、下一次何時」。
  pacerRetries: {},

  // EVAS finish 的 5 秒可撤銷候選。倒數完成前不寫 time_finished、不送 EOS；
  // event 保留原始 at_sec，正式完成時才能寫醫師真正說出 finish 的時間。
  pendingFinish: null,

  // 最後一次由語音寫進 cleanScale 的值（'1'~'4'）。只服務畫面上的「語音填入」
  // 標記——真正的值以 examinations.cleanScale 為準，醫師手動改了就以手動為準。
  cleansingVoiceValue: null,
  // transport 重送時，seq 不大於這個 watermark 的舊語音不得覆寫手動值。
  cleansingManualAfterSeq: 0,

  // --- 病理圖鑑（臨床輔助助理面板）---
  //
  // atlasCatalog 是 KKASR /atlas 的目錄（[{command, title, imageUrl, available}]），
  // 開場拉一次；atlasCommand 是「現在該顯示哪一本」，由語音指令 last-wins 決定。
  //
  // ⚠️ 圖鑑狀態刻意**不**跟著 sessionStarted 清空：它與這場收音無關，是醫師隨時
  //    可以呼叫的參考資料。收音重開時把醫師正在看的圖抽掉沒有道理。
  atlasCatalog: [],
  atlasCommand: null,
  // 已經**消費過**的最後一個 command seq（不是「已 append」的）。兩者不同：
  // appendCommands 一次把整批塞進 s.commands，consumeCommands 才逐筆處理，
  // 而處理途中有 await。clearAtlas 要的是「醫師已經看得到結果」的那條線 —— 見該 action。
  consumedCommandSeq: 0,

  // 醫師用 × 關掉圖鑑當下的最後一個 command seq。transport 若重送舊 atlas_*，
  // seq ≤ 這個值的一律不再顯示；後續新講的 seq 更大，照常顯示。
  atlasClearedSeq: 0,

  // --- 截圖語音編輯 ---
  //
  // KKASR 的 lesion 詞表（/key_info_terms 的 lesion 類別）。用來從 refine 那串
  // 攤平的 keywords 裡分出「哪個是病灶名」——不能用「剩下的都算」，那會把
  // sessile / biopsy / good 全寫進卡片。開場拉一次，理由同 atlasCatalog。
  //
  // ⚠️ 前端不自己維護病灶名清單：醫院現場往 key_info_terms.json 加一個病灶名，
  //    不該還要重編前端才會生效。
  lesionTerms: [],
  keyInfoCategories: {},

  // 醫師在卡片上按「結束編輯」的秒數（相對 session 起點）。滑鼠不會產生 KKASR
  // 指令，所以這裡自己補一個等效的 photo_over 進區間重算（見 getAsrPhotoWindows）。
  manualPhotoOvers: [],
  manualPhotoSelects: [],
  captureHandledSeq: 0,
  captureRequests: [],

  // 開場那兩支輔助資料（/atlas、/key_info_terms）重試完仍失敗。
  // KKASR 重啟的瞬間開頁就會踩到——原本只有 console.warn，醫師整場都不知道
  // 病灶名寫不進卡片、圖鑑喊了沒反應。要讓畫面講出來，並在補載成功後自動消失。
  atlasLoadFailed: false,
  lesionTermsLoadFailed: false,

  // --- LLM（只用來判斷「跑完了沒」，內容以 DB 為準）---
  //
  // ⚠️ Key Info 面板**不吃**這裡：LLM 的欄位抽取要 EOS 之後才跑，檢查途中永遠
  //    是空的。面板改吃每句 refine 自帶的 keywords（KKASR 的 key_info_extractor
  //    即時算的），LLM extract 只服務報告工作站的 Impression（走 webhook 落 DB）。
  extractReady: false,
  reportReady: false,
  llmFailure: null,
  reportAggregate: { status: 'pending', reason: 'not_started' },
  reportWaitStartedAtMs: null,
})

const state = initialState()

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

/**
 * 指數退避重試一個「拉一次就好」的載入。
 *
 * 針對的情境很具體：KKASR 剛重啟、還沒把 HTTP 端點掛起來的那幾秒鐘。開頁時序
 * 撞上這個窗口的機率不低，而代價是整場檢查的病灶名寫不進卡片。三次之間總共等
 * 1.5 秒（0.5 + 1.0；第三次失敗就直接放棄，不會再等），之後交給獨立的長週期
 * 重試計時器（見 retryTermsIfNeeded）。
 *
 * @param {() => Promise<any>} load 成功要回值，失敗一律 throw
 * @param {string} label 只用來寫 console
 * @returns {Promise<any|null>} null = 重試完仍失敗
 */
async function fetchWithRetry(load, label) {
  for (let attempt = 0; attempt < TERMS_RETRY_TIMES; attempt += 1) {
    try {
      return await load()
    } catch (err) {
      const last = attempt === TERMS_RETRY_TIMES - 1
      console.warn(`[asrSession] ${label}載入失敗（第 ${attempt + 1} 次）`, err)
      if (last) return null
      await sleep(TERMS_RETRY_BASE_MS * Math.pow(2, attempt))
    }
  }
  return null
}

/**
 * 詞表載入失敗時才開一條低頻的重試計時器（成功之後 retryTermsIfNeeded 自己收掉）。
 *
 * 用計時器而不是搭 pollOnce 的便車：pollOnce 需要 sessionId，而「KKASR 剛重啟」
 * 這個最典型的失敗成因會讓 session 也建不起來——掛在輪詢上等於永遠不會重試。
 */
function ensureTermsRetryTimer(dispatch) {
  if (termsTimer) return
  termsTimer = window.setInterval(() => dispatch('retryTermsIfNeeded'), TERMS_RETRY_INTERVAL_MS)
}

/**
 * 把滑鼠按的「結束編輯」轉成與 KKASR 指令同形狀的事件，好一起丟進區間重算。
 *
 * 形狀要一致（command / at_sec）才不必在 photoEditWindows 裡分辨來源——那支
 * 函式的職責是「把事件攤成區間」，誰產生的事件不關它的事。
 */
function manualPhotoOverEvents(s) {
  // source:'manual' 不是裝飾——滑鼠收尾的秒數取自「已 commit 的 refine」，天生
  // 落後於醫師按鈕的真實時刻，photoEditWindowRange 要靠這個旗標補回落後量
  return s.manualPhotoOvers.map((atSec) => ({
    command: 'photo_over',
    at_sec: atSec,
    param: null,
    source: 'manual',
  }))
}

function manualPhotoSelectEvents(s) {
  return s.manualPhotoSelects.map((item) => ({
    command: 'photo_select',
    at_sec: item.atSec,
    param: item.param,
    source: 'capture',
  }))
}

function persistedOverrides(s) {
  return {
    atlasClearedSeq: s.atlasClearedSeq,
    photoOverAtSec: s.manualPhotoOvers,
    photoSelects: s.manualPhotoSelects,
    captureHandledSeq: s.captureHandledSeq,
    captureRequests: s.captureRequests,
    cleansingManualAfterSeq: s.cleansingManualAfterSeq,
  }
}

const getters = {
  getAsrSessionId: (s) => s.sessionId,
  getAsrStatus: (s) => s.status,
  getAsrError: (s) => s.error,
  getAsrConflict: (s) => s.conflictSession,
  getAsrTruncated: (s) => s.truncated,
  getAsrReportReady: (s) => s.reportReady,
  getAsrLlmFailure: (s) => s.llmFailure,
  getAsrReportAggregate: (s) => s.reportAggregate,
  getAsrCommands: (s) => s.commands,
  getAsrHasAcceptedGo: (s) => s.hasAcceptedGo,
  getAsrStartedAtMs: (s) => s.startedAtMs,
  getAsrPacerTiming: (s) => s.pacerTiming,
  getAsrPacerWarning: (s) => s.pacerWarning,
  getAsrPacerDroppedCommands: (s) => s.pacerDroppedCommands,
  getAsrDraft: (s) => s.draft,
  getAsrFinalTranscript: (s) => s.finalTranscript,
  getAsrCaptureRequests: (s) => s.captureRequests,

  /** 收音中（含 EOS 前的所有狀態），用來擋掉「重複開始」與語系切換 */
  isAsrRecording: (s) => s.status === 'recording',

  /**
   * 全部逐字稿。
   *
   * 保留 backend cursor 的 append-only ledger，供診斷模式檢視每次 publication；
   * 正常畫面另由 getAsrVisibleLines 收斂同音訊區間的最新版本。
   */
  getAsrAllLines: (s) => s.lines,

  /** 5 秒可撤銷的 finish candidate（非 null 時顯示中央倒數框） */
  getAsrPendingFinish: (s) => s.pendingFinish,

  /** 最後一次語音填入的 Cleansing Level（'1'~'4'），沒有則 null */
  getAsrCleansingVoiceValue: (s) => s.cleansingVoiceValue,

  /**
   * 目前該顯示的圖鑑（{ command, title, imageUrl }），沒有則 null。
   *
   * 查不到目錄項就回 null（＝面板顯示空狀態）：atlas_clear 天生不在目錄裡，
   * 而 KKASR 加了新指令、前端目錄還沒重拉時也會走到這裡——兩種情況都該是
   * 「不顯示圖」，而不是拿一個破圖 URL 去請求。
   */
  getAsrAtlas: (s) => {
    if (!s.atlasCommand) return null
    const hit = s.atlasCatalog.find((entry) => entry.command === s.atlasCommand)
    return hit && hit.available ? hit : null
  },

  getAsrLesionTerms: (s) => s.lesionTerms,
  getAsrKeyInfoCategories: (s) => s.keyInfoCategories,

  /**
   * 開場輔助資料載入失敗時要給醫師看的一行警示，正常時空字串。
   *
   * 兩支各自降級的內容不同，所以分開講——只說「載入失敗」醫師不知道現在少了
   * 什麼功能、該不該改用手動補。
   */
  getAsrTermsWarning: (s) => {
    const lost = []
    if (s.lesionTermsLoadFailed) lost.push('截圖口述病灶名')
    if (s.atlasLoadFailed) lost.push('病理圖鑑')
    if (!lost.length) return ''
    return `KKASR 詞表載入失敗，${lost.join('、')}暫時停用（連線恢復後會自動補載）`
  },

  /**
   * 音訊時間軸上的「現在」——已知最後一句逐字稿／最後一個指令的秒數。
   *
   * ⚠️ 刻意**不用** Date.now()：整個模組的狀態都是從指令與逐字稿重算出來的，
   *    摻進牆上時鐘就不冪等了（F5 之後同一份資料會算出不同的畫面）。
   *
   * ⚠️ **它必然落後於「真正的現在」**，而且醫師剛講完的那一刻落後最多——refine
   *    的首次 commit 至少要 3 × 2.0 = 6 秒新音訊、未 commit 視窗上限 12 秒
   *    （`KKASR/KKApp/config.ini` 的 `[extend_window]`）。所以**不能**把它當成
   *    「醫師此刻在音訊時間軸上的位置」直接用：
   *      - 當「編輯區間是否已逾時」的比較基準 → 可以（保守方向：晚一點才判逾時）
   *      - 當「滑鼠收尾的時間點」→ 必須補回落後量，否則區間會塌成零寬、把醫師
   *        剛講完的整段丟掉（見 dictationFields 的 REFINE_COMMIT_LAG_SEC）
   */
  getAsrNowSec: (s) => {
    let now = 0
    for (const line of s.lines) {
      const end = Number(line && (line.end != null ? line.end : line.start))
      if (Number.isFinite(end) && end > now) now = end
    }
    for (const event of s.commands) {
      const at = Number(event && event.at_sec)
      if (Number.isFinite(at) && at > now) now = at
    }
    return now
  },

  /**
   * 截圖語音編輯的區間，`[{ param, startSec, endSec|null }]`。
   *
   * ⚠️ 從本頁收到的**完整指令清單**重算，不是逐筆累加；F5 不重播歷史 command，
   *    拍照自動開啟與滑鼠覆寫由 sessionStorage 恢復。詳見 utils/dictationFields。
   *
   * 卡片上按「結束編輯」產生的等效 photo_over 在這裡併進去——重算的輸入是
   * 「KKASR 指令 + 本場的滑鼠覆寫」，兩者都會在 F5 後被還原（覆寫存
   * sessionStorage），所以冪等性不變。
   */
  getAsrPhotoWindows: (s) => photoEditWindows(
    s.commands.concat(manualPhotoSelectEvents(s), manualPhotoOverEvents(s))
  ),

  /**
   * 現在正在編輯哪一張（還沒收尾的那一段），沒有就 null。
   * 卡片的藍底、以及口述內容要寫進哪一張，都看這個。
   *
   * 「還開著」有兩個條件：沒有收尾指令，**而且**還沒到自動關閉的上限——否則
   * 忘了講結束詞的那張卡片會一直反藍，看起來還在收，實際上已經不寫了。
   */
  getAsrPhotoEditing: (s, g) => {
    const windows = photoEditWindows(
      s.commands.concat(manualPhotoSelectEvents(s), manualPhotoOverEvents(s))
    )
    const last = windows.length ? windows[windows.length - 1] : null
    if (!last || last.endSec !== null) return null
    return g.getAsrNowSec < photoEditWindowRange(last).to ? last : null
  },

  /**
   * 這一場有沒有實質內容 —— 決定離開頁面時要送 EOS 還是 abort。
   *
   * 舊版問的是「講過 EVAS GO 沒有」。指令移除後改問「ASR 到底吐出東西沒有」：
   * 自動收音之後「點進來看一眼就離開」是常態，那種 session 一句逐字稿都沒有，
   * 送 EOS 只會讓 KKLLM 空跑一輪、再往 examination_transcripts 塞一列空白紀錄。
   */
  getAsrHasContent: (s) => s.lines.length > 0,

  getAsrCommandCursor: (s) => s.commandCursor,

  /**
   * 畫面要顯示的逐字稿 —— 全部。
   *
   * 正式 LLM 輸入由 KKASR evas crop 約束；畫面仍可顯示監聽期 draft。
   * 代價是進頁面後的閒聊也會被收進來，這是移除 EVAS GO 時明確接受的取捨。
   */
  getAsrVisibleLines: (s) => {
    const final = s.finalTranscript
    if (!final) return currentRefineLineProjection(s.lines)
    const segments = Array.isArray(final.segments) && final.segments.length
      ? final.segments
      : [{ start: 0, end: null, text: final.text || '' }]
    return segments.map((segment, index) => ({
      ...segment,
      seq: index + 1,
      committed_text: segment.text,
      final_revision: final.revision,
      final_status: final.status,
    }))
  },

  /**
   * 目前的配速員 step（0 = session 還沒建立）。
   *
   * finish 那一步（3）只在**確定結束**後才算數——待確認狀態下仍顯示退鏡中，
   * 因為醫師隨時可能繼續。
   */
  getAsrPacerStep: (s) => PACER_PHASE_STEPS[s.pacerTiming.phase] || 0,
}

const mutations = {
  setEndpoints(s, { httpUrl, wsUrl }) {
    s.httpUrl = httpUrl
    s.wsUrl = wsUrl
  },

  setSourceMode(s, mode) {
    s.sourceMode = mode
  },

  setStatus(s, status) {
    s.status = status
  },

  setError(s, error) {
    s.error = error
  },

  setConflict(s, record) {
    s.conflictSession = record
  },

  sessionStarted(s, { sessionId, examinationId, startedAtMs }) {
    s.sessionId = sessionId
    s.examinationId = examinationId == null ? null : String(examinationId)
    s.startedAtMs = startedAtMs
    s.status = 'recording'
    s.error = null
    s.conflictSession = null
    // 新 session＝新的一場檢查，前一場的逐字稿與里程碑不能沿用
    s.lines = []
    s.finalTranscript = null
    s.draft = null
    s.refineCursor = 0
    s.truncated = false
    s.commands = []
    s.commandCursor = 0
    s.hasAcceptedGo = false
    s.pacerApplied = {}
    s.pacerTiming = { phase: 'listening' }
    s.pacerWarning = null
    s.pacerDroppedCommands = []
    s.pendingPacerEvents = []
    s.pacerRetries = {}
    s.consumedCommandSeq = 0
    s.pendingFinish = null
    s.cleansingVoiceValue = null
    s.cleansingManualAfterSeq = 0
    s.manualPhotoOvers = []
    s.manualPhotoSelects = []
    s.captureHandledSeq = 0
    s.captureRequests = []
    s.atlasClearedSeq = 0
    s.extractReady = false
    s.reportReady = false
    s.llmFailure = null
    s.reportAggregate = { status: 'pending', reason: 'not_started' }
    s.reportWaitStartedAtMs = null
  },

  /** terminal readback 只建立畫面身分，不宣稱恢復錄音或啟動任何 transport。 */
  sessionRecovered(s, payload) {
    mutations.sessionStarted(s, payload)
    s.status = 'finalizing'
  },

  /** F5 之後把本場的滑鼠覆寫併回來（見 utils/asrSessionOverrides） */
  restoreOverrides(s, {
    atlasClearedSeq,
    photoOverAtSec,
    photoSelects,
    captureHandledSeq,
    captureRequests,
    cleansingManualAfterSeq,
  }) {
    s.atlasClearedSeq = Number(atlasClearedSeq) || 0
    s.manualPhotoOvers = Array.isArray(photoOverAtSec) ? photoOverAtSec.slice() : []
    s.manualPhotoSelects = Array.isArray(photoSelects) ? photoSelects.slice() : []
    s.captureHandledSeq = Number(captureHandledSeq) || 0
    s.captureRequests = Array.isArray(captureRequests) ? captureRequests.slice() : []
    s.cleansingManualAfterSeq = Number(cleansingManualAfterSeq) || 0
  },

  addManualPhotoOver(s, atSec) {
    s.manualPhotoOvers = s.manualPhotoOvers.concat(Number(atSec) || 0)
  },

  addManualPhotoSelect(s, item) {
    s.manualPhotoSelects = s.manualPhotoSelects.concat(item)
  },

  setCaptureHandledSeq(s, seq) {
    const value = Number(seq)
    if (Number.isFinite(value) && value > s.captureHandledSeq) s.captureHandledSeq = value
  },

  enqueueCaptureRequest(s, payload) {
    const event = payload && payload.event
    const seq = Number(event && event.seq)
    if (!Number.isFinite(seq) || s.captureRequests.some((item) => Number(item.seq) === seq)) return
    s.captureRequests = s.captureRequests.concat({
      ...event,
      captureSessionId: payload.sessionId == null ? null : String(payload.sessionId),
      captureExaminationId: payload.examinationId == null ? null : String(payload.examinationId),
    }).sort((a, b) => Number(a.seq) - Number(b.seq))
  },

  dequeueCaptureRequest(s, identity) {
    const value = Number(identity && identity.seq)
    const sessionId = String(identity && identity.sessionId || '')
    const examinationId = String(identity && identity.examinationId || '')
    s.captureRequests = s.captureRequests.filter((item) => !(
      Number(item.seq) === value
      && String(item.captureSessionId || '') === sessionId
      && String(item.captureExaminationId || '') === examinationId
    ))
  },

  setDraft(s, draft) {
    s.draft = draft && String(draft.text || '').trim() ? { ...draft } : null
  },

  setConsumedCommandSeq(s, seq) {
    const value = Number(seq)
    if (Number.isFinite(value) && value > s.consumedCommandSeq) s.consumedCommandSeq = value
  },

  setAtlasClearedSeq(s, seq) {
    s.atlasClearedSeq = Number(seq) || 0
  },

  setTermsLoadFailed(s, { atlas, lesion }) {
    if (typeof atlas === 'boolean') s.atlasLoadFailed = atlas
    if (typeof lesion === 'boolean') s.lesionTermsLoadFailed = lesion
  },

  appendLines(s, { items, nextAfterSeq, truncated }) {
    if (items && items.length) {
      s.lines = s.lines.concat(items.map((item) => {
        const normalized = { ...item }
        const keyInfoUi = item && item.low_confidence === true
          ? normalizeKeyInfoUi(item.key_info_ui, 'refine_low_confidence')
          : null
        if (keyInfoUi) normalized.key_info_ui = keyInfoUi
        else delete normalized.key_info_ui
        const fastCorrection = normalizeFastCorrection(item && item.fast_correction)
        if (fastCorrection) normalized.fast_correction = fastCorrection
        else delete normalized.fast_correction
        return normalized
      }))
    }
    if (typeof nextAfterSeq === 'number') s.refineCursor = nextAfterSeq
    if (truncated) s.truncated = true
  },

  setFinalTranscript(s, snapshot) {
    if (!snapshot || snapshot.status !== 'ai_final_unreviewed') return
    if (String(snapshot.session_id || '') !== String(s.sessionId || '')) return
    const currentRevision = Number(s.finalTranscript && s.finalTranscript.revision) || 0
    const nextRevision = Number(snapshot.revision) || 0
    if (nextRevision <= currentRevision) return
    const normalized = { ...snapshot }
    const keyInfoUi = normalizeKeyInfoUi(snapshot.key_info_ui, 'final_transcript')
    if (keyInfoUi) normalized.key_info_ui = keyInfoUi
    else delete normalized.key_info_ui
    s.finalTranscript = normalized
    s.draft = null
  },

  appendCommands(s, { items, nextAfterSeq }) {
    if (items && items.length) {
      s.commands = s.commands.concat(items)
      if (items.some((item) => item && item.command === 'evas_go')) s.hasAcceptedGo = true
    }
    if (typeof nextAfterSeq === 'number') s.commandCursor = nextAfterSeq
  },

  /** F5 只恢復同 session 的 GO 存在性，不把歷史 command 送回副作用 consumer。 */
  restoreAcceptedGo(s, { sessionId, accepted }) {
    if (String(s.sessionId || '') !== String(sessionId || '')) return
    s.hasAcceptedGo = accepted === true
  },

  setCleansingVoiceValue(s, value) {
    s.cleansingVoiceValue = value
  },

  setCleansingManualAfterSeq(s, seq) {
    s.cleansingManualAfterSeq = Number(seq) || 0
  },

  setLesionTerms(s, terms) {
    s.lesionTerms = Array.isArray(terms) ? terms : []
  },

  setKeyInfoCategories(s, categories) {
    s.keyInfoCategories = categories && typeof categories === 'object' ? { ...categories } : {}
    s.lesionTerms = Array.isArray(s.keyInfoCategories.lesion) ? s.keyInfoCategories.lesion : []
  },

  setAtlasCatalog(s, items) {
    s.atlasCatalog = items || []
  },

  /** null / atlas_clear = 收起圖鑑 */
  setAtlasCommand(s, command) {
    s.atlasCommand = command || null
  },

  setPendingFinish(s, pending) {
    s.pendingFinish = pending
  },

  setPacerTiming(s, timing) {
    s.pacerTiming = timing ? { ...timing } : { phase: 'listening' }
    // 這筆提示指的問題真的被處理掉了（缺的補上了、錯的改掉了）→ 提示自然消失。
    // 收斂點只放這裡：hydrate（F5）、correction、milestone 成功全部會經過它，
    // 三處各寫一次清除邏輯遲早會漏掉一處。兩種 kind 的判準不同，見 isPacerDroppedResolved。
    if (!s.pacerDroppedCommands.length) return
    s.pacerDroppedCommands = s.pacerDroppedCommands.filter(
      (item) => !isPacerDroppedResolved(item, s.pacerTiming),
    )
  },

  setPacerWarning(s, warning) {
    s.pacerWarning = warning || null
  },

  /**
   * 記一筆「這個里程碑出事了，而且不會自己好」。
   *
   * 同一個 command 只留最新一筆：重複講同一個指令不該在畫面上疊出一排。
   * kind=wrongValue 必須帶著當時看到的 point，insertion_to 另帶 server 值與本次預期
   * 的時間／位置，才能讓提示只在兩欄都符合本次提交後收斂。缺少這些資料時，提示會在
   * 下一次 setPacerTiming 自我蒸發——寧可不記，也不要記一筆假的。
   */
  recordPacerDroppedCommand(s, {
    command,
    message,
    kind = PACER_DROPPED_MISSING,
    point = null,
    insertionLevel,
    expectedPoint,
    expectedInsertionLevel,
    nowMs = Date.now(),
  } = {}) {
    if (!PACER_STEPS[command] || !message) return
    if (kind === PACER_DROPPED_WRONG_VALUE && !hasPoint(point)) {
      console.warn(`[asrSession] ${command} 的 wrongValue 提示缺少 point，不予記錄`)
      return
    }
    const entry = { command, message, kind, point, atMs: nowMs }
    if (command === 'insertion_to' && insertionLevel !== undefined) {
      entry.insertionLevel = insertionLevel
    }
    if (command === 'insertion_to' && expectedPoint !== undefined) {
      entry.expectedPoint = expectedPoint
    }
    if (command === 'insertion_to' && expectedInsertionLevel !== undefined) {
      entry.expectedInsertionLevel = expectedInsertionLevel
    }
    s.pacerDroppedCommands = upsertPacerDropped(
      s.pacerDroppedCommands,
      entry,
    )
  },

  /** 醫師看過了、自己關掉。這是除了「里程碑被填上」以外唯一的清除途徑。 */
  dismissPacerDroppedCommand(s, command) {
    s.pacerDroppedCommands = s.pacerDroppedCommands.filter((item) => item.command !== command)
  },

  queuePacerEvent(s, event) {
    const command = event && event.command
    if (!PACER_STEPS[command]) return
    if (s.pendingPacerEvents.some((item) => item.command === command)) return
    s.pendingPacerEvents = s.pendingPacerEvents.concat({ ...event })
      .sort((a, b) => PACER_STEPS[a.command] - PACER_STEPS[b.command])
  },

  dequeuePacerEvent(s, command) {
    s.pendingPacerEvents = s.pendingPacerEvents.filter((item) => item.command !== command)
    if (s.pacerRetries[command] == null) return
    const rest = { ...s.pacerRetries }
    delete rest[command]
    s.pacerRetries = rest
  },

  /**
   * 記一次「可重送的失敗」：累加次數並排下一次退避時間。
   * 次數用完就讓它出佇列——留著只會每秒重打，畫面上的警示已經在了。
   */
  recordPacerRetryFailure(s, { command, nowMs = Date.now() } = {}) {
    if (!PACER_STEPS[command]) return
    const attempts = ((s.pacerRetries[command] || {}).attempts || 0) + 1
    if (attempts >= PACER_RETRY_TIMES) {
      console.warn(`[asrSession] 配速指令 ${command} 重送 ${attempts} 次仍失敗，停止重送`)
      s.pendingPacerEvents = s.pendingPacerEvents.filter((item) => item.command !== command)
      const rest = { ...s.pacerRetries }
      delete rest[command]
      s.pacerRetries = rest
      // 放棄重送＝這筆臨床時刻就此沒了，與 4xx 一樣屬終局失敗（里程碑是空的 →
      // kind=missing），要留在畫面上，不能只靠會被下一個成功指令洗掉的 pacerWarning。
      s.pacerDroppedCommands = upsertPacerDropped(s.pacerDroppedCommands, {
        command,
        message: `配速指令 ${command} 重送 ${attempts} 次仍失敗，已停止重送。`
          + '請用配速員的「修改」手動補上這個時間點。',
        kind: PACER_DROPPED_MISSING,
        point: null,
        atMs: nowMs,
      })
      return
    }
    s.pacerRetries = {
      ...s.pacerRetries,
      [command]: {
        attempts,
        nextAttemptAtMs: nowMs + PACER_RETRY_BASE_MS * (2 ** (attempts - 1)),
      },
    }
  },

  markPacerApplied(s, command) {
    // Vue 2 的物件新增屬性要走 $set 才有反應性
    s.pacerApplied = { ...s.pacerApplied, [command]: true }
  },

  setExtractReady(s, ready) {
    s.extractReady = !!ready
  },

  setReportReady(s, ready) {
    s.reportReady = !!ready
  },

  setLlmFailure(s, failure) {
    s.llmFailure = failure || null
  },

  setReportAggregate(s, aggregate) {
    s.reportAggregate = aggregate || { status: 'pending', reason: 'not_started' }
  },

  startReportWait(s, startedAtMs) {
    if (s.reportWaitStartedAtMs == null) s.reportWaitStartedAtMs = Number(startedAtMs)
  },

  resetSession(s) {
    Object.assign(s, initialState(), { httpUrl: s.httpUrl, wsUrl: s.wsUrl })
  },
}

const actions = {
  /** 從 runtime setting 解析 KKASR 位址；元件建 WSClient 時也要用 wsUrl */
  configure({ commit, rootGetters }) {
    const endpoints = resolveKkasrEndpoints(rootGetters.getSetting)
    commit('setEndpoints', endpoints)
    return endpoints
  },

  /** 三個 final fetch 入口共用；401/403 不得再被當成尚未完成。 */
  handleFinalAuthRequired({ state: s, commit, dispatch }, { sessionId, examinationId }) {
    const expectedSessionId = String(sessionId || '')
    const expectedExaminationId = String(examinationId || '')
    if (
      !expectedSessionId
      || !expectedExaminationId
      || String(s.sessionId || '') !== expectedSessionId
      || String(s.examinationId || '') !== expectedExaminationId
    ) return { handled: false, stale: true }

    const recoveryMarked = markFinalAuthRecovery({
      sessionId: expectedSessionId,
      examinationId: expectedExaminationId,
    })
    const error = recoveryMarked
      ? FINAL_AUTH_ERROR
      : `${FINAL_AUTH_ERROR}（此分頁無法保存恢復身分，請勿離開目前頁面）`
    commit('setLlmFailure', { kind: 'auth', error })
    commit('setError', error)
    commit('setStatus', 'error')
    dispatch('stopLlmPolling')
    return { handled: true, authRequired: true, recoveryMarked, error }
  },

  /**
   * 登入恢復後只讀 exact terminal 三件組；不依賴 active registry，也不建立新 ASR。
   */
  async recoverFinalAuthSession({ state: s, commit, dispatch, rootGetters }, { examinationId }) {
    const inspected = inspectFinalAuthRecovery()
    if (inspected.status === 'absent') return { recovered: false }
    if (inspected.status !== 'valid') {
      return {
        recovered: false,
        blocked: true,
        recoveryRejected: true,
        reason: inspected.status,
        error: inspected.status === 'expired'
          ? '本場登入恢復資訊已逾時；為避免開到錯場，請回檢查清單確認後再進入'
          : '本場登入恢復資訊無效；為避免開到錯場，請回檢查清單確認後再進入',
      }
    }
    const marker = inspected.marker
    const expectedExaminationId = String(examinationId || '')
    if (marker.examinationId !== expectedExaminationId) {
      clearFinalAuthRecovery()
      return {
        recovered: false,
        blocked: true,
        recoveryRejected: true,
        reason: 'examination_mismatch',
        error: '登入恢復資訊屬於另一筆檢查，未載入資料也未開始新的收音',
      }
    }

    const epoch = storeEpoch
    const isStale = () => {
      if (epoch !== storeEpoch) return true
      const data = rootGetters && rootGetters['examinations/getExaminationData']
      const visible = data && data.examination
      return !!(visible && String(visible.id) !== expectedExaminationId)
    }
    const api = new KkasrApi(s.httpUrl)
    let finalResponse
    try {
      finalResponse = await api.finalTranscriptLatest(marker.sessionId, marker.examinationId)
    } catch (err) {
      if (isStale()) return { recovered: false, stale: true }
      return {
        recovered: false,
        blocked: true,
        error: '無法讀取本場既有結果，請確認登入後再重新整理一次',
      }
    }
    if (isStale()) return { recovered: false, stale: true }

    if (isAuthHttpResponse(finalResponse)) {
      return { recovered: false, blocked: true, authRequired: true, error: FINAL_AUTH_ERROR }
    }

    const finalBody = finalResponse && finalResponse.body
    const finalRevision = Number(finalBody && finalBody.revision)
    const finalValid = !!(
      finalResponse && finalResponse.ok
      && finalBody
      && finalBody.status === 'ai_final_unreviewed'
      && String(finalBody.session_id || '') === marker.sessionId
      && (!finalBody.examination_id || String(finalBody.examination_id) === marker.examinationId)
      && Number.isSafeInteger(finalRevision)
      && finalRevision > 0
      && typeof finalBody.text === 'string'
      && finalBody.text.trim()
    )
    if (!finalValid) {
      return {
        recovered: false,
        blocked: true,
        error: '本場完整逐字稿尚未完成或身分不一致，未載入任何資料；請稍後重新整理',
      }
    }

    // final與同revision summary是同一份snapshot，核對成功就一起發布；下游
    // report/extract 尚在pending或壞掉都不能把已成立的authoritative final扣住。
    commit('sessionRecovered', {
      sessionId: marker.sessionId,
      examinationId: marker.examinationId,
      startedAtMs: marker.markedAtMs,
    })
    commit('setFinalTranscript', finalBody)

    let reportResponse
    let extractResponse
    try {
      [reportResponse, extractResponse] = await Promise.all([
        api.llmReportLatest(marker.sessionId),
        api.llmExtractLatest(marker.sessionId),
      ])
    } catch (err) {
      if (isStale()) return { recovered: true, sessionId: marker.sessionId, stale: true }
      commit('setStatus', 'finalizing')
      dispatch('startLlmPolling')
      return {
        recovered: true,
        sessionId: marker.sessionId,
        downstreamPending: true,
      }
    }
    if (isStale()) return { recovered: true, sessionId: marker.sessionId, stale: true }

    const report = classifyLlmReadback(reportResponse, marker.sessionId)
    const extract = classifyLlmReadback(extractResponse, marker.sessionId)
    if (report.status === 'auth' || extract.status === 'auth') {
      const auth = await dispatch('handleFinalAuthRequired', {
        sessionId: marker.sessionId,
        examinationId: marker.examinationId,
      })
      return {
        recovered: true,
        sessionId: marker.sessionId,
        downstreamBlocked: true,
        ...auth,
      }
    }

    commit('setReportReady', report.status === 'succeeded')
    commit('setExtractReady', extract.status === 'succeeded')
    const failed = report.status === 'failed'
      ? { kind: 'report', error: report.body.error || 'LLM 報告產生失敗' }
      : (extract.status === 'failed'
          ? { kind: 'extract', error: extract.body.error || 'LLM 欄位擷取失敗' }
          : (report.status === 'invalid'
              ? { kind: 'report', error: `本場報告狀態無法核對（HTTP ${report.httpStatus}）` }
              : (extract.status === 'invalid'
                  ? { kind: 'extract', error: `本場欄位擷取狀態無法核對（HTTP ${extract.httpStatus}）` }
                  : null)))
    if (failed) {
      commit('setLlmFailure', failed)
      commit('setError', failed.error)
      commit('setStatus', 'error')
      dispatch('stopLlmPolling')
    } else if (report.status === 'pending' || extract.status === 'pending') {
      commit('setStatus', 'finalizing')
      dispatch('startLlmPolling')
    } else {
      commit('setStatus', 'done')
      dispatch('stopLlmPolling')
    }
    return {
      recovered: true,
      sessionId: marker.sessionId,
      terminalFailed: !!failed,
      downstreamPending: !failed && (
        report.status === 'pending' || extract.status === 'pending'
      ),
      error: failed && failed.error,
    }
  },

  /**
   * 頁面載入時先問 KKASR「現在在收誰的音」。
   *
   * F5 後只有同分頁留下的短效 marker，加上 server 回傳的同 session／同病例
   * identity 與可接續 lifecycle，才允許恢復。REFINE 從 cursor 0 一次載回；FAST
   * 只清除、不落 state，舊 command 只推進 cursor 而不重播 side effect。
   *
   * @returns {Promise<{resumed:boolean, sessionId?:string}>}
   */
  async resumeExistingSession({ state: s, commit, dispatch, rootGetters }, { examinationId }) {
    const marker = loadSessionReloadResume()
    if (!marker) return { resumed: false }
    if (marker.examinationId !== String(examinationId || '')) {
      clearSessionReloadResume()
      return { resumed: false }
    }

    const epoch = storeEpoch
    const isStale = () => {
      if (epoch !== storeEpoch) return true
      const data = rootGetters && rootGetters['examinations/getExaminationData']
      const visible = data && data.examination
      return !!(visible && String(visible.id) !== String(examinationId || ''))
    }
    const api = new KkasrApi(s.httpUrl)
    let res
    try {
      res = await api.currentSession()
    } catch (err) {
      return { resumed: false, blocked: true, error: '無法確認 F5 前的 ASR session，請再重新整理一次' }
    }

    if (isStale()) return { resumed: false, stale: true }
    const body = res && res.body
    if (!res.ok || !body || !body.session_id) {
      return { resumed: false, blocked: true, error: 'F5 前的 ASR session 已不存在' }
    }
    const classify = (candidate) => {
      const identityMatches = (
        String(candidate && candidate.session_id || '') === marker.sessionId
        && String(candidate && candidate.examination_id || '') === marker.examinationId
      )
      const sourceMode = ['mic', 'file'].includes(candidate && candidate.source_mode)
        ? candidate.source_mode
        : null
      const live = candidate && candidate.status === 'asr_active' && candidate.eos_accepted !== true
      const finalizing = candidate && (
        (candidate.status === 'asr_finalizing' || candidate.status === 'llm_running')
        && candidate.eos_accepted === true
      )
      return {
        identityMatches,
        sourceMode,
        mode: live ? 'live' : (finalizing ? 'finalizing' : null),
      }
    }
    const initial = classify(body)
    if (!initial.identityMatches) {
      clearSessionReloadResume()
      return { resumed: false }
    }
    if (!initial.mode) {
      clearSessionReloadResume()
      return {
        resumed: false,
        blocked: true,
        error: 'F5 前的 ASR session 已結束或中止，不會自動恢復',
      }
    }
    if (!initial.sourceMode) {
      return { resumed: false, blocked: true, error: 'F5 session 缺少有效音訊來源，已停止恢復' }
    }

    // 恢復前先做三個一次性快照：
    //   * FAST 是 pop snapshot，只讀掉、不 commit，避免草稿跨 F5 復活。
    //   * REFINE 從 cursor 0 讀一次，正式逐字稿與 Key Info 只 append 一份。
    //   * command 只取尾端 cursor、不 replay，避免舊硬體／DB side effects 再執行。
    let fastResponse
    let refineResponse
    let commandResponse
    try {
      [fastResponse, refineResponse, commandResponse] = await Promise.all([
        api.fastLatest(body.session_id),
        api.refineSince(body.session_id, 0),
        api.commandsSince(body.session_id, 0),
      ])
    } catch (err) {
      return { resumed: false, blocked: true, error: 'F5 逐字稿恢復失敗，請再重新整理一次' }
    }
    if (isStale()) return { resumed: false, stale: true }
    const refineBody = refineResponse && refineResponse.body
    let commandBody = commandResponse && commandResponse.body
    const fastBody = fastResponse && fastResponse.body
    const refineItems = refineBody && refineBody.items
    const responsesValid = (
      fastResponse && fastResponse.ok
      && (!fastBody || !fastBody.session_id || String(fastBody.session_id) === marker.sessionId)
      && refineResponse && refineResponse.ok
      && refineBody && String(refineBody.session_id || '') === marker.sessionId
      && Array.isArray(refineItems)
      && refineItems.every((item) => !item.session_id || String(item.session_id) === marker.sessionId)
      && commandResponse && commandResponse.ok
      && commandBody && String(commandBody.session_id || '') === marker.sessionId
      && Array.isArray(commandBody.items)
      && commandBody.items.every((item) => !item.session_id || String(item.session_id) === marker.sessionId)
      && Number.isFinite(Number(commandBody.last_seq))
      && Number.isFinite(Number(commandBody.next_after_seq))
      && Number(commandBody.last_seq) >= Number(commandBody.next_after_seq)
    )
    if (!responsesValid) {
      return { resumed: false, blocked: true, error: 'F5 session 回應身分不一致，已停止恢復' }
    }

    // `/commands` 每頁最多 200 筆。恢復不執行 command，但仍要把 chronology 讀到
    // snapshot 尾端：pending finish 可能已被後續 continue 取消，cursor 也必須直接
    // 推到 last_seq，否則第 201 筆會在 fresh polling 被當成新 side effect 重播。
    const commandItems = commandBody.items.slice()
    let commandPageCursor = Number(commandBody.next_after_seq)
    let commandLastSeq = Number(commandBody.last_seq)
    let commandPages = 1
    try {
      while (commandPageCursor < commandLastSeq) {
        if (commandPages >= 50) throw new Error('command snapshot 超過安全分頁上限')
        const page = await api.commandsSince(marker.sessionId, commandPageCursor)
        const pageBody = page && page.body
        const nextCursor = Number(pageBody && pageBody.next_after_seq)
        const nextLastSeq = Number(pageBody && pageBody.last_seq)
        if (
          !page || !page.ok || !pageBody
          || String(pageBody.session_id || '') !== marker.sessionId
          || !Array.isArray(pageBody.items)
          || !pageBody.items.every((item) => (
            !item.session_id || String(item.session_id) === marker.sessionId
          ))
          || !Number.isFinite(nextCursor)
          || !Number.isFinite(nextLastSeq)
          || nextCursor <= commandPageCursor
          || nextLastSeq < nextCursor
        ) throw new Error('command snapshot 分頁不一致')
        commandItems.push(...pageBody.items)
        commandPageCursor = nextCursor
        commandLastSeq = Math.max(commandLastSeq, nextLastSeq)
        commandPages += 1
      }
    } catch (err) {
      return { resumed: false, blocked: true, error: 'F5 command chronology 恢復失敗，已停止恢復' }
    }
    if (isStale()) return { resumed: false, stale: true }

    // Snapshot 完成後再問一次 current。這道 publication gate 擋住 snapshot 期間
    // 被 force takeover 的窄窗；任何 state 都在此核對通過後才 commit。
    let confirmedResponse
    try {
      confirmedResponse = await api.currentSession()
    } catch (err) {
      return { resumed: false, blocked: true, error: 'F5 session 最終複核失敗，已停止恢復' }
    }
    if (isStale()) return { resumed: false, stale: true }
    const confirmedBody = confirmedResponse && confirmedResponse.body
    const confirmed = classify(confirmedBody)
    if (
      !confirmedResponse || !confirmedResponse.ok
      || !confirmed.identityMatches || !confirmed.mode || !confirmed.sourceMode
    ) {
      clearSessionReloadResume()
      return { resumed: false, blocked: true, error: 'F5 session 已在恢復期間切換，未發布舊資料' }
    }
    if (confirmed.mode === 'live' && confirmed.sourceMode === 'file' && !marker.fileResume) {
      return { resumed: false, blocked: true, error: 'F5 前的音檔身分或播放位置已遺失' }
    }
    let fileReceipt = null
    if (confirmed.mode === 'live' && confirmed.sourceMode === 'file') {
      const acceptedRaw = confirmedBody.accepted_audio_samples
      const offsetRaw = confirmedBody.accepted_file_offset_samples
      const acceptedSamples = Number(acceptedRaw)
      const offsetSamples = Number(offsetRaw)
      const sampleRate = Number(confirmedBody.audio_sample_rate) || 16000
      const streamEndReceived = confirmedBody.audio_stream_end_received === true
      const targetSamples = streamEndReceived
        ? Number(confirmedBody.audio_target_samples)
        : null
      if (
        acceptedRaw == null
        || offsetRaw == null
        || !Number.isSafeInteger(acceptedSamples)
        || acceptedSamples < 0
        || !Number.isSafeInteger(offsetSamples)
        || offsetSamples < 0
        || sampleRate <= 0
        || (streamEndReceived && (
          !Number.isSafeInteger(targetSamples)
          || targetSamples <= 0
          || targetSamples !== acceptedSamples
        ))
      ) {
        return {
          resumed: false,
          blocked: true,
          error: '伺服器缺少可驗證的 File Mode 收樣位置，請重新開始本次音檔',
        }
      }
      fileReceipt = {
        acceptedSamples,
        offsetSamples,
        sampleRate,
        streamEndReceived,
        targetSamples,
      }
    }

    const eventSeq = Number(marker.pendingFinish && marker.pendingFinish.event
      && marker.pendingFinish.event.seq)
    const pendingMarkerSeq = Number.isFinite(eventSeq) ? eventSeq : Number(marker.commandCursor)
    const pendingCancelled = Number.isFinite(pendingMarkerSeq) && commandItems.some((event) => (
      Number(event && event.seq) > pendingMarkerSeq
      && event.command === 'evas_continue'
    ))
    const restoredPendingFinish = (
      Number.isFinite(pendingMarkerSeq)
      && pendingMarkerSeq <= commandLastSeq
      && !pendingCancelled
    ) ? marker.pendingFinish : null

    // startedAtMs 取 KKASR 回的 created_at（session 建立的 wall clock，秒）。
    // 這一步是配速員在 F5 之後仍然正確的關鍵——指令事件的 at_sec 是相對音訊
    // 時間軸的秒數，起點錯了整條計時線就跟著錯。用 Date.now() 當起點會讓
    // 重整前就講過的 cecum 被算成「剛剛才講」。
    const startedAtMs = Number(confirmedBody.created_at) > 0
      ? Number(confirmedBody.created_at) * 1000
      : Date.now()

    commit('sessionStarted', {
      sessionId: confirmedBody.session_id,
      examinationId: confirmedBody.examination_id,
      startedAtMs,
    })
    commit('setSourceMode', confirmed.sourceMode)
    // 重整前的滑鼠覆寫與待完成拍照 queue 要在新 polling 前還原；歷史 command
    // 本次不重播，因此不會把已關閉的 UI 或已執行的 side effect 再打開。
    commit('restoreOverrides', loadSessionOverrides(body.session_id))
    pruneSessionOverrides(body.session_id)
    commit('appendLines', {
      items: refineItems,
      nextAfterSeq: refineBody.next_after_seq,
      truncated: refineBody.truncated,
    })
    // 只推進 cursor，不把舊 command 放回 state/consumer。
    commit('appendCommands', {
      items: [],
      nextAfterSeq: commandLastSeq,
    })
    commit('restoreAcceptedGo', {
      sessionId: confirmedBody.session_id,
      accepted: commandItems.some((event) => event && event.command === 'evas_go'),
    })
    if (confirmed.mode === 'live' && restoredPendingFinish) {
      commit('setPendingFinish', restoredPendingFinish)
    }
    if (confirmed.mode === 'finalizing') commit('setStatus', 'finalizing')
    clearSessionReloadResume()

    // F5 可能發生在第一次 background bind 尚未抵達 Node 前；server 端同值冪等，
    // 因此仍在收音／finalizing 的正式 session 會安全補送一次 exact identity。
    if (confirmedBody.require_procedure_context === true) {
      dispatch('bindProcedureContext', {
        sessionId: confirmedBody.session_id,
        examinationId: confirmedBody.examination_id,
      })
    }

    // DB 時間點才是正本；本次不 replay commands，但畫面仍須取得同病例的正式快照。
    const pacerHydration = await dispatch('hydratePacerTiming')
    if (isStale() || (pacerHydration && pacerHydration.stale)) {
      return { resumed: false, stale: true }
    }

    const resumed = {
      resumed: true,
      sessionId: confirmedBody.session_id,
      mode: confirmed.mode,
      sourceMode: confirmed.sourceMode,
    }
    if (confirmed.mode === 'live' && confirmed.sourceMode === 'file') {
      resumed.acceptedSamples = fileReceipt.acceptedSamples
      resumed.audioSampleRate = fileReceipt.sampleRate
      resumed.audioStreamEndReceived = fileReceipt.streamEndReceived
      resumed.audioTargetSamples = fileReceipt.targetSamples
      resumed.fileResume = {
        ...marker.fileResume,
        acceptedSamples: fileReceipt.acceptedSamples,
        offsetSec: fileReceipt.offsetSamples / fileReceipt.sampleRate,
      }
    }
    return resumed
  },

  /**
   * 建立收音 session。
   *
   * force=false 時，若 KKASR 已有另一場檢查在收音會回 409——**不靜默接管**，
   * 把 conflict 交給畫面顯示，由使用者決定要不要踢掉對方。
   *
   * @returns {Promise<{ok:boolean, conflict?:object, sessionId?:string}>}
   */
  async startSession({ state: s, commit, dispatch }, { sourceMode, examinationId, accNo, force = false }) {
    const startEpoch = storeEpoch
    const startOwner = {
      sessionId: String(s.sessionId || ''),
      examinationId: String(s.examinationId || ''),
    }
    const startIsStale = () => (
      startEpoch !== storeEpoch
      || String(s.sessionId || '') !== startOwner.sessionId
      || String(s.examinationId || '') !== startOwner.examinationId
    )
    commit('setSourceMode', sourceMode)
    commit('setStatus', 'starting')
    commit('setError', null)
    commit('setConflict', null)

    const api = new KkasrApi(s.httpUrl)
    let res
    try {
      // Mic Mode 優先用 crop 守住有效 EVAS GO 區間；若 ASR 沒辨識到有效 GO，
      // KKASR 才回退整場 final。File Mode 是使用者明確選取的完整音檔，維持不開
      // crop；兩種來源之後都走同一道 deterministic control filtering。
      const evasCrop = sourceMode !== 'file'
      res = await api.startSession({
        sourceMode,
        examinationId,
        accNo,
        force,
        evasCrop,
        requireProcedureContext: true,
      })
    } catch (err) {
      commit('setStatus', 'error')
      commit('setError', `連不上 KKASR（${s.httpUrl}）`)
      return { ok: false }
    }

    if (res.status === 409) {
      commit('setStatus', 'conflict')
      commit('setConflict', (res.body && res.body.active_session) || {})
      return { ok: false, conflict: (res.body && res.body.active_session) || {} }
    }

    if (!res.ok || !res.body || !res.body.session_id) {
      commit('setStatus', 'error')
      commit('setError', (res.body && res.body.message) || `session 建立失敗 HTTP ${res.status}`)
      return { ok: false }
    }

    // 新 client 可能在分段部署／回退期間遇到尚未支援 procedure-context 的舊 KKApp。
    // 舊版會忽略未知 request 欄位並回成功；若只看 session_id，後續就會靜默使用
    // legacy colonoscopy prior。transport 尚未開始，故只中止這次回應中的 exact session，
    // 並要求使用者更新服務後重試；正常 required echo 不等待 DB late-bind。
    if (res.body.require_procedure_context !== true) {
      const createdSessionId = String(res.body.session_id)
      let cleanupConfirmed = false
      try {
        const cleanup = await api.streamingOff(createdSessionId)
        const cleanupBody = cleanup && cleanup.body
        cleanupConfirmed = !!(
          cleanup
          && cleanup.ok
          && cleanupBody
          && cleanupBody.status === 'aborted'
          && String(cleanupBody.session_id || '') === createdSessionId
          && cleanupBody.eos_accepted !== true
        )
      } catch (err) {
        console.warn('[asrSession] incompatible session cleanup failed', err)
      }
      // exact abort 是後端資源清理，仍要等它 settle；但頁面已 teardown／另場已接手時，
      // 舊 promise 只能回 stale receipt，不能把新場 store 覆寫成版本錯誤。
      if (startIsStale()) return { ok: false, stale: true, cleanupConfirmed }
      const error = cleanupConfirmed
        ? 'KKASR 版本不相容，已中止本次 session；請更新服務後重試'
        : 'KKASR 版本不相容，且無法確認本次 session 已中止；請停止操作並聯絡管理員'
      commit('setStatus', 'error')
      commit('setError', error)
      return { ok: false, error, cleanupConfirmed }
    }

    // startedAtMs 取「送出請求後拿到回應」的當下。KKASR 的 at_sec 是相對音訊
    // 時間軸的秒數，起點就是 session 建立那一刻，誤差是一次 HTTP round-trip。
    commit('sessionStarted', {
      sessionId: res.body.session_id,
      examinationId,
      startedAtMs: Date.now(),
    })
    // 新的一場：上一場的滑鼠覆寫一律不沿用（sessionId 不同，本來也讀不到），
    // 順手把舊 key 掃掉，免得同一個分頁看一整天病例累積到撞配額
    pruneSessionOverrides(res.body.session_id)

    // 報告前置條件必須在任何 WebSocket／音源／draft polling 前精確成立。公開流程曾在
    // 這裡吃到 401 卻繼續送完整音訊，最後只能產生 procedure_context_required；因此
    // 本次只等待這一個 exact bind，失敗就中止剛建立且尚未收樣的 session。
    const bindResult = await dispatch('bindProcedureContext', {
      sessionId: res.body.session_id,
      examinationId,
    })
    const createdIsStale = () => (
      startEpoch !== storeEpoch
      || String(s.sessionId || '') !== String(res.body.session_id)
      || String(s.examinationId || '') !== String(examinationId)
    )
    if (createdIsStale() || (bindResult && bindResult.stale)) {
      // 頁面可能在 bind await 期間離開；此時 store 已不是 owner，不能再 commit，
      // 但這個 exact 新場仍是本 request 建立且從未收樣，必須直接回後端中止。
      let cleanupConfirmed = false
      try {
        const cleanup = await api.streamingOff(String(res.body.session_id))
        const body = cleanup && cleanup.body
        cleanupConfirmed = !!(
          cleanup && cleanup.ok
          && body
          && body.status === 'aborted'
          && String(body.session_id || '') === String(res.body.session_id)
          && body.eos_accepted !== true
        )
      } catch (err) {
        console.warn('[asrSession] stale pre-audio session cleanup failed', err)
      }
      return { ok: false, stale: true, cleanupConfirmed }
    }
    if (!bindResult || !bindResult.ok) {
      const cleanup = await dispatch('abortSession', { sessionId: res.body.session_id })
      if (createdIsStale() || (cleanup && cleanup.stale)) {
        return { ok: false, stale: true, cleanupConfirmed: !!(cleanup && cleanup.ok) }
      }
      const authRequired = !!(
        bindResult && [401, 403].includes(Number(bindResult.status))
      )
      const error = authRequired
        ? PROCEDURE_CONTEXT_AUTH_ERROR
        : ((cleanup && cleanup.ok)
            ? '報告前置條件建立失敗，已中止本次 session；請稍後再試'
            : '報告前置條件建立失敗，且無法確認本次 session 已中止；請停止操作並聯絡管理員')
      commit('setStatus', 'error')
      commit('setError', error)
      return {
        ok: false,
        authRequired,
        error,
        cleanupConfirmed: !!(cleanup && cleanup.ok),
      }
    }
    // 同病例真的建立了新場後，舊 terminal auth marker 不可在下一次 F5 搶回舊場。
    clearFinalAuthRecovery()

    // 建 session 只代表「開始聽」，不代表臨床檢查開始。time_start 等 EVAS GO。
    await dispatch('hydratePacerTiming')

    dispatch('startTranscriptPolling')
    return { ok: true, sessionId: res.body.session_id }
  },

  async bindProcedureContext({ state: s }, { sessionId, examinationId }) {
    const epoch = storeEpoch
    const expectedSessionId = String(sessionId || '')
    const expectedExaminationId = String(examinationId || '')
    const isStale = () => (
      epoch !== storeEpoch
      || String(s.sessionId || '') !== expectedSessionId
      || String(s.examinationId || '') !== expectedExaminationId
    )
    if (!expectedSessionId || !expectedExaminationId || isStale()) {
      return { ok: false, stale: true }
    }
    try {
      const body = await evas2ReportServices.bindProcedureContext({
        examinationID: expectedExaminationId,
        sessionId: expectedSessionId,
      })
      if (isStale()) return { ok: false, stale: true }
      const ok = body
        && body.status === 'procedure_context_ready'
        && String(body.session_id || '') === expectedSessionId
        && String(body.examination_id || '') === expectedExaminationId
      if (!ok) {
        console.warn('[asrSession] procedure context bind returned invalid identity')
        return { ok: false, status: body && body.status }
      }
      return { ok: true, procedureType: body.procedure_type }
    } catch (err) {
      if (isStale()) return { ok: false, stale: true }
      console.warn('[asrSession] procedure context bind failed', err)
      return {
        ok: false,
        status: err && err.status,
        authRequired: [401, 403].includes(Number(err && err.status)),
      }
    }
  },

  /**
   * FAST draft 走獨立 250ms 節奏；正式 refine / command cursor 仍維持 1 秒。
   * 兩者分開後，慢的 cursor 請求不會把灰字首顯一起卡住。
   */
  async pollDraftOnce({ state: s, commit }) {
    if (!s.sessionId || draftPollInFlight) return
    const epoch = storeEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    const isStale = () =>
      epoch !== storeEpoch
      || s.sessionId !== sessionId
      || s.examinationId !== examinationId

    const api = new KkasrApi(s.httpUrl)
    draftPollInFlight = true
    try {
      const { ok, body } = await api.fastLatest(sessionId)
      if (!isStale() && ok) commit('setDraft', body)
    } catch (err) {
      console.warn('[asrSession] fast draft poll failed', err)
    } finally {
      draftPollInFlight = false
    }
  },

  /**
   * cursor 拉一次逐字稿與指令。
   *
   * ⚠️ **不可重入**。這支被每秒一次的 setInterval 叫，中間有兩次 await；KKASR
   *    慢回應（>1 秒）時下一 tick 會帶著**還沒推進的 cursor** 再撈一次，同一批
   *    逐字稿與 Key Info 就會被 append 兩遍，畫面上出現重複行。前一次還沒跑完
   *    就跳過這一 tick——cursor 沒推進，該收的下一輪一樣收得到。
   *
   * ⚠️ **await 回來之後要重驗自己還算不算數**。入口那道 `sessionId` 檢查只擋得住
   *    「發出前就沒有 session」，擋不住「發出後才換了一場」：醫師在輪詢 in-flight
   *    時離開 /exam2（teardown）再進另一場檢查，**甲病人的逐字稿會被 append 進
   *    乙病人的 state**。請求最長會拖到 10 秒（見 KkasrApi 的逾時），兩場檢查之間
   *    切換不到 10 秒是做得到的。
   *
   * @returns {Promise<{ended:boolean}>} ended=true 代表 EVAS finish 已由 server 接受
   */
  async pollOnce({ state: s, commit, dispatch }) {
    if (!s.sessionId) return { ended: false }
    if (pollInFlight) return { ended: false }
    // 發起當下的身分：世代（teardown 會推進）＋ sessionId（同一頁換一場也會變）。
    // 兩個都要——teardown 之後可能又建了新 session，只比 sessionId 會漏掉
    // 「新的一場剛好拿到相同 id」這種理論情況，只比世代則漏掉同頁換場。
    const epoch = storeEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    const isStale = () =>
      epoch !== storeEpoch
      || s.sessionId !== sessionId
      || s.examinationId !== examinationId

    const api = new KkasrApi(s.httpUrl)
    let ended = false

    // ⚠️ 旗標的設定與 try 之間**不留任何敘述**：中間插一行就是「那一行拋了就永遠
    //    卡在 true」的縫。finally 的釋放保證是「teardown 不必也不該再清一次」的
    //    唯一依據（見 teardown），所以這個保證必須是無條件的。
    pollInFlight = true
    try {
      try {
        const { ok, body } = await api.refineSince(sessionId, s.refineCursor)
        if (isStale()) return { ended: false }
        if (ok && body && Array.isArray(body.items)) {
          const applied = await appendRefineItemsWithRawRender({
            state: s,
            commit,
            items: body.items,
            nextAfterSeq: body.next_after_seq,
            truncated: body.truncated,
            isStale,
          })
          if (!applied) return { ended: false }
        }
      } catch (err) {
        // 網路瞬斷不該讓輪詢停掉；cursor 沒推進，下一輪自然補回來
        console.warn('[asrSession] refine poll failed', err)
      }

      try {
        const { ok, body } = await api.commandsSince(sessionId, s.commandCursor)
        if (isStale()) return { ended: false }
        if (ok && body && Array.isArray(body.items) && body.items.length) {
          commit('appendCommands', { items: body.items, nextAfterSeq: body.next_after_seq })
          const result = await dispatch('consumeCommands', body.items)
          ended = !!(result && result.ended)
        } else if (ok && body) {
          commit('appendCommands', { items: [], nextAfterSeq: body.next_after_seq })
        }
      } catch (err) {
        console.warn('[asrSession] commands poll failed', err)
      }

    } finally {
      // 一定要放掉：漏一次就再也不會有下一輪輪詢，整頁看起來像 ASR 掛了
      pollInFlight = false
    }

    // 配速補償在主輪詢臨界區外跑；server 慢回應不能卡住逐字稿與新 command。
    //
    // ⚠️ 這裡用 console.error 而非 warn，而且明確印出 stack：這支是 fire-and-forget，
    //    它自己拋出的**程式錯誤**（例如少解構一個 mutation → ReferenceError）不會讓
    //    任何測試或畫面變紅，只會靜靜地把「重送」整條路廢掉。實際發生過一次，
    //    當時的 console.warn 在線上幾乎沒人看得到。
    dispatch('flushPendingPacerEvents').catch((err) => {
      console.error('[asrSession] 配速員重送失敗', (err && err.stack) || err)
    })

    return { ended }
  },

  /**
   * 詞表／圖鑑目錄先前載入失敗 → 隔一段時間再試一輪。
   *
   * ⚠️ 這支**不掛在 pollOnce 上**，走自己的計時器（見 ensureTermsRetryTimer），
   *    兩個理由：
   *    1. pollOnce 第一行就要求 `sessionId`。而「詞表載不到」最典型的成因正是
   *       KKASR 剛重啟——那時 `session/start` 也會失敗、根本沒有 sessionId，
   *       掛在輪詢上等於**永遠不會重試**，警示會一直掛著到醫師重整頁面。
   *    2. 它會等 KKASR 的 HTTP 回應（而且 KKASR 有問題正是它被觸發的前提），
   *       放進主輪詢的互斥臨界區就是拿次要依賴的故障去癱瘓主線。
   */
  async retryTermsIfNeeded({ state: s, dispatch }) {
    if (!s.atlasLoadFailed && !s.lesionTermsLoadFailed) {
      // 兩支都好了就把計時器收掉，正常情況下不留背景輪詢
      termsTimer = clearInterval_(termsTimer)
      return
    }
    // 上一輪還在等 KKASR 回應（它可能接受連線卻不回話）→ 這一輪跳過，不堆疊。
    // ⚠️ 節流只留這一層：先前還疊了一個 `Date.now() < termsRetryAtMs` 的時間戳
    //    節流，與 10 秒的 interval 等價但會互相踩到（interval 剛好落在時間戳
    //    之前一點點時整輪被跳過 → 實際變成 20 秒才重試一次）。
    if (termsRetryInFlight) return

    termsRetryInFlight = true
    try {
      if (s.atlasLoadFailed) await dispatch('fetchAtlasCatalog')
      if (s.lesionTermsLoadFailed) await dispatch('fetchLesionTerms')
    } catch (err) {
      console.warn('[asrSession] 詞表重試失敗', err)
    } finally {
      termsRetryInFlight = false
    }
  },

  /** 從 examinations DB 還原配速 phase；F5 時必須在 command replay 前完成。 */
  async hydratePacerTiming({ state: s, commit, rootGetters }, payload = {}) {
    const examinationId = payload.examinationId || s.examinationId
    if (!examinationId) return { ok: false }
    const sessionId = s.sessionId
    const sessionExaminationId = s.examinationId
    const isStale = () => {
      if (
        sessionExaminationId
        && (s.sessionId !== sessionId || s.examinationId !== sessionExaminationId)
      ) return true
      const data = rootGetters['examinations/getExaminationData']
      const visible = data && data.examination
      return !!(visible && String(visible.id) !== String(examinationId))
    }
    try {
      const body = await evas2PacerServices.fetch(examinationId)
      if (isStale()) return { ok: false, stale: true }
      const timing = body && body.result
      if (!timing) throw new Error('配速員回應缺少 result')
      commit('setPacerTiming', timing)
      commit('setPacerWarning', null)
      commit('examinations/applyPacerTiming', timing, { root: true })
      return { ok: true, timing }
    } catch (err) {
      if (isStale()) return { ok: false, stale: true }
      const warning = `配速員狀態載入失敗：${(err && err.message) || err}`
      commit('setPacerWarning', warning)
      return { ok: false, error: warning }
    }
  },

  /**
   * 手動修正配速員里程碑。
   *
   * 這支刻意不要求 sessionId：報告頁回到檢查頁時屬於 correction mode，ASR 已經
   * EOS，仍然要能修正時間與 insertion 位置。四個時間點一次交給 server transaction
   * 驗證及重算，避免舊做法逐欄寫入時留下「時間點已改、衍生時間尚未改」的半套狀態。
   */
  async correctPacerTiming({ state: s, commit, rootGetters }, payload = {}) {
    const currentExamination = () => {
      const data = rootGetters['examinations/getExaminationData']
      return data && data.examination
    }
    const examination = currentExamination()
    const examinationId = payload.examinationId
      || s.examinationId
      || (examination && examination.id)
    if (!examinationId) return { ok: false }
    const sessionExaminationId = s.examinationId == null || s.examinationId === ''
      ? null
      : String(s.examinationId)

    const requestIdentity = normalizePacerCorrectionIdentity(payload.correctionIdentity)
    const currentIdentity = () => {
      if (typeof payload.getCurrentCorrectionIdentity !== 'function') return null
      try {
        return normalizePacerCorrectionIdentity(payload.getCurrentCorrectionIdentity())
      } catch (err) {
        return null
      }
    }
    const isCurrentRequest = () => {
      const visibleExamination = currentExamination()
      return !!visibleExamination
        && String(visibleExamination.id) === String(examinationId)
        && (sessionExaminationId == null
          ? (s.examinationId == null || s.examinationId === '')
          : String(s.examinationId) === sessionExaminationId
            && sessionExaminationId === String(examinationId))
        && requestIdentity
        && requestIdentity.examinationId === String(examinationId)
        && samePacerCorrectionIdentity(requestIdentity, currentIdentity())
    }
    // 缺 route/case identity 不能降級成「只要沒有明確不相等就算同一頁」。
    if (!isCurrentRequest()) return { ok: false, stale: true }

    try {
      const body = await evas2PacerServices.correct({
        examinationID: examinationId,
        points: payload.points,
        insertionLevel: payload.insertionLevel,
      })
      if (!isCurrentRequest()) return { ok: false, stale: true }
      const timing = body && body.result
      if (!body || body.changed !== true || !isCompletePacerTimingSnapshot(timing)) {
        throw new Error('配速員回應不完整')
      }
      commit('setPacerTiming', timing)
      commit('setPacerWarning', null)
      commit('examinations/applyPacerTiming', timing, { root: true })
      return { ok: true, timing }
    } catch (err) {
      if (!isCurrentRequest()) return { ok: false, stale: true }
      const warning = pacerCorrectionWarning(err)
      commit('setPacerWarning', warning)
      return { ok: false, error: warning }
    }
  },

  /** 將一個 ASR event 換成牆鐘時間，交由 server 原子 transition。 */
  async applyPacerCommand({ state: s, commit }, { event, queueOnFailure = true }) {
    const command = event && event.command
    if (!PACER_STEPS[command] || !s.examinationId) return { accepted: false }
    const sessionId = s.sessionId
    const examinationId = s.examinationId

    const atSec = Number(event.at_sec || 0)
    const time = msToHHmmss((s.startedAtMs || Date.now()) + atSec * 1000)
    const param = event && event.param
    const insertionLevel = command === 'insertion_to'
      ? (param && typeof param === 'object' ? param.id : param)
      : undefined

    try {
      const body = await evas2PacerServices.milestone({
        examinationID: examinationId,
        command,
        time,
        insertionLevel,
      })
      if (s.sessionId !== sessionId || s.examinationId !== examinationId) {
        return { accepted: false, stale: true }
      }
      const timing = body && body.result
      // milestone 與 correction 同樣是完整 snapshot publication；partial 2xx 不得先
      // 污染 store、mark/dequeue 或播放成功音，再靠後續 hydrate 猜測補齊。
      if (!isCompletePacerTimingSnapshot(timing)) {
        const invalidSnapshot = new Error('配速員回應快照不完整')
        invalidSnapshot.retryable = true
        throw invalidSnapshot
      }

      // first-wins 的沉默陷阱：ASR 在醫師進檢查頁時就開始收音，早於 EVAS GO，
      // 所以「還沒開始就被誤觸的里程碑」是真的到得了 DB 的。等醫師真的喊出
      // 那個指令時，server 回 { changed: false } 保留誤觸的舊值，而這條路徑
      // 一路 accepted: true、清警示、markPacerApplied ——醫師收到零訊號，
      // 病歷上留的是誤觸時刻。里程碑允許缺格之後這種組合才變得可達（ADR 0007）。
      //
      // 判準用「server 保留的時刻 ≠ 我剛剛送出的時刻」，不是「changed 為 false」：
      // 重播、poll retry、F5 重送同一筆都會 changed: false，但那些時刻是一樣的，
      // 屬於正常的冪等，不該打擾醫師。
      const point = timing[PACER_POINT_FIELDS[command]]
      // insertion_to 的成功音是 publication receipt：不能只相信 changed=true，而要核對
      // server 回傳的「時間＋canonical position」確實就是本次提交值。其他命令維持
      // ADR 0007 的 first-wins 判準，只在 changed=false 且時刻不同時視為 wrong-value。
      const insertionResponseMismatch = command === 'insertion_to'
        && (point !== time || timing.insertionLevel !== insertionLevel)
      const otherFirstWinsMismatch = command !== 'insertion_to'
        && !body.changed && hasPoint(point) && point !== time
      commit('setPacerTiming', timing)
      commit('setPacerWarning', null)
      commit('markPacerApplied', command)
      commit('dequeuePacerEvent', command)
      commit('examinations/applyPacerTiming', timing, { root: true })
      if (insertionResponseMismatch || otherFirstWinsMismatch) {
        // kind=wrongValue：這個欄位「有值」，錯的是值本身。用 missing 的收斂規則
        // （欄位被填上就清掉）會讓提示被下一個成功的里程碑洗掉，等於又回到靜默丟棄。
        const insertionMissing = command === 'insertion_to' && !hasPoint(point)
        commit('recordPacerDroppedCommand', {
          command,
          kind: insertionMissing ? PACER_DROPPED_MISSING : PACER_DROPPED_WRONG_VALUE,
          point,
          insertionLevel: command === 'insertion_to' ? timing.insertionLevel : undefined,
          expectedPoint: command === 'insertion_to' ? time : undefined,
          expectedInsertionLevel: command === 'insertion_to' ? insertionLevel : undefined,
          message: command === 'insertion_to'
            ? `insertion_to 回傳 ${point || '未記錄時間'}／位置 ${timing.insertionLevel || '未設定'}`
              + `，與本次 ${time}／位置 ${insertionLevel || '未設定'} 不一致；未播放成功提示音。`
              + '請用配速員的「修改」確認正確時間與位置。'
            : `${command} 已記錄為 ${point}，你剛才說的 ${time} 沒有寫入`
              + '（同一個里程碑只採計第一次）。若既有值是誤觸，請用配速員的「修改」改成正確時間。',
        })
      } else if (
        (command === 'evas_go' || command === 'insertion_to' || command === 'evas_finish')
        && event.source !== 'manual'
      ) {
        // server 已接受這一筆語音事件才播放；音效服務自行收斂 autoplay / play 失敗，
        // 不 await，避免瀏覽器媒體能力反過來阻斷里程碑、EOS 或導頁。
        voiceCommandCues.playAccepted({
          sessionId,
          eventId: event.seq,
          command,
          source: event.source,
        })
      }
      return { accepted: true, changed: !!body.changed, timing }
    } catch (err) {
      if (s.sessionId !== sessionId || s.examinationId !== examinationId) {
        return { accepted: false, stale: true }
      }
      // 4xx 是 server 對這筆語意的明確裁定（錯序、參數不合法、病例不存在），
      // 重送必然拿到同一個答案。硬要重送就會變成每秒一次的 409 迴圈，既洗掉
      // 後端 log 也讓醫師的畫面永遠停在同一則警示。這種失敗只能由醫師用手動
      // 修正處理，所以直接讓它出佇列，並在警示裡講清楚要怎麼救。
      const retryable = isRetryableRequestError(err)
      const reason = (err && err.message) || err
      const status = Number(err && err.status)
      // 401/403 的救法不是「用修改補上」——那一樣會被擋掉。要先把登入弄回來。
      const expired = status === 401 || status === 403
      const warning = retryable
        ? `配速指令 ${command} 未套用（將自動重送）：${reason}`
        : expired
          ? `配速指令 ${command} 未套用：登入已逾期，請重新登入後再用配速員的「修改」補上（${reason}）`
          : `配速指令 ${command} 未套用，請用配速員的「修改」手動補上：${reason}`
      commit('setPacerWarning', warning)
      if (!retryable) {
        commit('dequeuePacerEvent', command)
        // 終局失敗另外落在 pacerDroppedCommands：pacerWarning 會被下一個成功的
        // 指令或 F5 的 hydrate 清成 null，這筆「臨床時刻掉了」的事實不能跟著消失。
        commit('recordPacerDroppedCommand', { command, message: warning })
      } else if (queueOnFailure) {
        commit('queuePacerEvent', event)
      }
      return { accepted: false, retryable, error: warning }
    }
  },

  async flushPendingPacerEvents({ state: s, commit, dispatch }) {
    // 沒有病例就沒有可送的對象。少了這道判斷，applyPacerCommand 會在第一行早退回
    // { accepted: false } 且不帶 retryable，於是既不計次也不出佇列——每秒空轉一次
    // dispatch 直到醫師離開頁面。
    if (!s.pendingPacerEvents.length || !s.examinationId || pacerFlushInFlight) return
    const epoch = storeEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    pacerFlushInFlight = true
    try {
      for (const event of s.pendingPacerEvents.slice()) {
        if (
          epoch !== storeEpoch
          || s.sessionId !== sessionId
          || s.examinationId !== examinationId
        ) return
        // 退避時間還沒到就整輪停手：里程碑要照 PACER_STEPS 的先後送，跳過它去
        // 送後面那筆只會把順序弄反。
        const retry = s.pacerRetries[event.command]
        if (retry && retry.nextAttemptAtMs > Date.now()) break
        const result = await dispatch('applyPacerCommand', { event, queueOnFailure: false })
        if (!result || !result.accepted) {
          // 可重送的失敗才計次；4xx 已在 applyPacerCommand 裡直接出佇列。
          if (!result || result.retryable) {
            commit('recordPacerRetryFailure', { command: event.command })
          }
          // 前一步尚未落地時後面的 transition 未必送得出去；留到下輪，不空打 API。
          break
        }
      }
    } finally {
      pacerFlushInFlight = false
    }
  },

  /**
   * 消費指令事件。四個配速事件走 server state machine：
   * evas_go → insertion_to → proximal_time → evas_finish。
   * Server 負責 first-wins 與錯序拒絕；前端只採用它回傳的完整快照。
   *
   *   cleansing_*  last-wins。與上面三個不同，清潔度是可以改口的判斷
   *                （沖洗完會重講），最後一次算數。見 CLEANSING_LEVEL_VALUES。
   *
   *   atlas_*      last-wins，純畫面狀態。醫師會來回對照 JNET 與 Kudo，最後
   *                講的那本才是現在要看的；atlas_clear 收起。不寫 DB、不進報告。
   *
   * 回傳 { ended: true } 讓呼叫端知道要送 EOS 並導向報告工作站——這件事牽涉
   * WebSocket 與麥克風，屬於元件的職責，不在 store 裡做。
   */
  async consumeCommands({ state: s, commit, dispatch }, events) {
    let ended = false

    // cursor 正常會排序，但 mixed batch 的 client contract 不依賴 transport；先按 seq
    // 排好，capture watermark 才不會因 11 先到而把同批合法的 10 吃掉。
    const orderedEvents = (Array.isArray(events) ? events.slice() : [])
      .sort((a, b) => Number(a && a.seq) - Number(b && b.seq))
    for (const event of orderedEvents) {
      const command = event && event.command
      // 「處理到這一筆了」。擺在處理**之前**：這一行與同一輪的畫面更新之間沒有
      // await，所以「已消費」與「醫師看得到」是同一條線。clearAtlas 靠它分辨
      // 「× 關掉的是哪些」——已 append 但還沒輪到的那些不算（見 clearAtlas）。
      commit('setConsumedCommandSeq', event && event.seq)

      // finish 先進 5 秒可撤銷視窗，倒數完成才會呼叫 server milestone。continue
      // 只在這個視窗內有作用；其他時間講到不改任何檢查狀態。
      if (command === 'evas_continue') {
        if (s.pendingFinish && !s.pendingFinish.committing) commit('setPendingFinish', null)
        continue
      }
      if (command === 'evas_finish') {
        await dispatch('requestPacerFinish', { event })
        continue
      }

      // Cleansing Level 不是配速員里程碑，先攔下來（它不在 PACER_STEPS 裡）
      if (CLEANSING_LEVEL_VALUES[command]) {
        const seq = Number(event && event.seq)
        if (Number.isFinite(seq) && seq <= s.cleansingManualAfterSeq) continue
        await dispatch('writeCleansingLevel', { command })
        continue
      }

      // 病理圖鑑：純畫面狀態，不寫 DB、不影響報告。last-wins，clear 就是清空。
      if (isAtlasCommand(command)) {
        // 醫師已經用 × 把圖鑑關掉了 → 那個時間點之前的 atlas_* 全部作廢。
        // 沒有這道 guard 的話，transport 重送會讓已關閉的圖鑑自己跳回畫面上。
        const seq = Number(event.seq)
        if (Number.isFinite(seq) && seq <= s.atlasClearedSeq) continue
        commit('setAtlasCommand', command === ATLAS_CLEAR_COMMAND ? null : command)
        continue
      }

      // 喚醒拍照是硬體 side effect：每個不同 seq 都進有序 queue；queue 與 watermark
      // 一起持久化，F5 會用同一 server 冪等鍵續送，不會漏掉也不會多建一張。
      if (command === 'photo_capture') {
        const seq = Number(event && event.seq)
        if (!Number.isFinite(seq) || seq <= s.captureHandledSeq) continue
        commit('enqueueCaptureRequest', {
          event,
          sessionId: s.sessionId,
          examinationId: s.examinationId,
        })
        commit('setCaptureHandledSeq', seq)
        saveSessionOverrides(s.sessionId, persistedOverrides(s))
        continue
      }

      // 截圖語音編輯（photo_select / image_annotate / photo_over）刻意**不在這裡處理**。
      //
      // 上面幾種指令都是「事件一來就做一件事」（寫一個 DB 欄位、換一張圖），
      // 這一組不是——它定義的是一段**區間**，而「現在在編輯哪一張、那段期間
      // 講了什麼」要看整份指令清單加上逐字稿才算得出來。做成 getter
      // （getAsrPhotoWindows / getAsrPhotoEditing）從本頁收到的完整清單重算；F5
      // 不重播歷史副作用，已開啟的圖片編輯區間另由 overrides 快照恢復。
      if (command === 'photo_select' || command === 'image_annotate' || command === 'photo_over') continue

      // 呼叫／選取會碰檢查頁 DOM 與 screenshots API，由 Exam2DetailPage 依完整
      // commands 清單串行消費。selection endpoint 是 set 語義，transport 重送仍安全。
      if (command === 'image_focus' || command === 'image_select') continue

      if (!PACER_STEPS[command]) continue
      const result = await dispatch('applyPacerCommand', { event })
      if (result && result.accepted && command === 'evas_finish') {
        ended = true
      }
    }

    return { ended }
  },

  /** 建立 5 秒 finish candidate；重複 finish 不延長原本的安全視窗。 */
  requestPacerFinish({ state: s, commit }, { event, nowMs = Date.now() }) {
    if (!s.sessionId || !s.examinationId || s.pendingFinish) return { accepted: false }
    if (s.pacerTiming.phase !== 'withdrawing') {
      // ⚠️ 這道 gate 本身刻意不放寬（server 端允許缺格，前端結束檢查仍要求已過脾曲，
      //    見 ADR 0007「取捨與邊界」）。但文案必須告訴醫師**下一步怎麼做**：
      //    只寫「目前階段為 inserting」，醫師看不出來要去哪裡補什麼。
      const warning = FINISH_BLOCKED_HINTS[s.pacerTiming.phase]
        || `配速指令 evas_finish 未套用：目前階段為 ${s.pacerTiming.phase}`
      commit('setPacerWarning', warning)
      return { accepted: false, error: warning }
    }
    const candidate = {
      event: { ...event, command: 'evas_finish' },
      atSec: Number(event && event.at_sec) || 0,
      deadlineMs: Number(nowMs) + FINISH_GRACE_MS,
      committing: false,
    }
    commit('setPendingFinish', candidate)
    commit('setPacerWarning', null)
    return { accepted: true, pending: candidate }
  },

  cancelPacerFinish({ state: s, commit }) {
    if (!s.pendingFinish || s.pendingFinish.committing) return { cancelled: false }
    commit('setPendingFinish', null)
    return { cancelled: true }
  },

  /** 倒數完成後才把候選 event 交給 server；失敗時清掉候選，保留原 ASR session。 */
  async finalizePacerFinish({ state: s, commit, dispatch }) {
    const pending = s.pendingFinish
    if (!pending || !pending.event) return { accepted: false }
    commit('setPendingFinish', { ...pending, committing: true })
    const result = await dispatch('applyPacerCommand', {
      event: pending.event,
      queueOnFailure: false,
    })
    commit('setPendingFinish', null)
    return result
  },

  /** 手動 Finish 是語音故障時的備援，也走同一個 5 秒安全視窗。 */
  async manualCommand({ dispatch }, { command, atSec }) {
    const canonical = command === 'finish' ? 'evas_finish' : command
    if (canonical === 'evas_finish') {
      return dispatch('requestPacerFinish', {
        event: { command: canonical, at_sec: atSec, source: 'manual' },
      })
    }
    const result = await dispatch('applyPacerCommand', {
      event: { command: canonical, at_sec: atSec, source: 'manual' },
      queueOnFailure: false,
    })
    return result
  },

  /**
   * 把語音講的 Cleansing Level 寫進 examinations.cleanScale。
   *
   * 落點與 CleansingLevelPanel 手動點選同一欄，所以報告端**零改動**：
   * Report2Preview → SummaryEnReport → ColonPreviewBowelCleanseForms 的
   * cleanse_level.before 會自己長出來。
   *
   * ⚠️ 與配速 milestone 相反，這裡是 last-wins：清潔度就是要能覆寫。
   *    F5 接續 session 時 cursor 從 0 重讀，全部 cleansing
   *    事件會重放一次，醫師若在語音之後手動改過那一格，會被重放的值蓋回去。
   *    手動調整時會記住當下已消費的 seq；F5 後較舊語音事件不再覆寫，
   *    但重整後新講的較大 seq 仍可 last-wins。
   *
   * ⚠️ 只在值真的不同時才打 API：重放時大多數事件的目標值與現況相同，
   *    無條件寫會在 F5 後對 DB 連打好幾發沒有意義的 update。
   */
  async writeCleansingLevel({ rootGetters, commit, dispatch }, { command }) {
    const value = CLEANSING_LEVEL_VALUES[command]
    if (!value) return

    const examData = rootGetters['examinations/getExaminationData']
    const examination = examData && examData.examination
    if (!examination || !examination.id) return

    commit('setCleansingVoiceValue', value)
    if (String(examination.cleanScale || '') === value) return

    await dispatch(
      'examinations/updateExaminationColumn',
      { id: examination.id, column: 'cleanScale', value },
      { root: true }
    )
  },

  /** 醫師手動改清腸度時，將語音重放邊界跟著 session 持久化。 */
  markManualCleansing({ state: s, commit }) {
    const watermark = Math.max(
      Number(s.cleansingManualAfterSeq) || 0,
      Number(s.consumedCommandSeq) || 0,
      Number(s.commandCursor) || 0
    )
    commit('setCleansingManualAfterSeq', watermark)
    commit('setCleansingVoiceValue', null)
    saveSessionOverrides(s.sessionId, persistedOverrides(s))
  },

  /**
   * 拉一次病理圖鑑目錄。開場呼叫；失敗會退避重試，仍失敗就掛警示等輪詢補。
   *
   * 拉失敗不擋任何事——面板維持空狀態，其餘功能照跑。但**不能再靜默**了：
   * KKASR 重啟的瞬間開頁，醫師會對著沒反應的圖鑑喊一整場而不知道為什麼。
   */
  async fetchAtlasCatalog({ state: s, commit, dispatch }) {
    const api = new KkasrApi(s.httpUrl)
    // 這一輪重試最長要跑 3 × 10 秒逾時 + 1.5 秒退避 ≈ 31.5 秒，醫師完全可能在這
    // 期間就離開檢查頁。記下發起時的世代，回來時對不上就什麼都不做——否則會在
    // 已經 teardown 的 store 上設旗標、甚至重開一條沒人收的重試計時器。
    const epoch = storeEpoch
    const items = await fetchWithRetry(async () => {
      const { ok, body } = await api.listAtlas()
      if (!ok || !body || !Array.isArray(body.items)) throw new Error('圖鑑目錄回應格式不符')
      return body.items
    }, '圖鑑目錄')

    if (epoch !== storeEpoch) return

    if (!items) {
      commit('setTermsLoadFailed', { atlas: true })
      ensureTermsRetryTimer(dispatch)
      return
    }

    commit(
      'setAtlasCatalog',
      items.map((item) => ({
        command: item.command,
        title: item.title,
        titleZh: item.title_zh || item.title,
        scopeZh: item.scope_zh || '',
        completeness: item.completeness || '',
        completenessNoteZh: item.completeness_note_zh || '',
        disclaimerZh: item.disclaimer_zh || '',
        // KKASR 回相對路徑，這裡補成瀏覽器載得到的絕對網址
        imageUrl: api.absoluteUrl(item.image_url),
        available: item.available !== false,
        // v2 可有多頁；若舊 KKASR 只回 image_url，就合成單頁讓新版 UI 仍可用。
        images: (Array.isArray(item.images) && item.images.length
          ? item.images
          : [{ image_url: item.image_url, available: item.available }]
        ).map((image, index) => ({
          id: image.id || `${item.command}-${index + 1}`,
          imageUrl: api.absoluteUrl(image.image_url),
          available: image.available !== false,
          captionZh: image.caption_zh || '',
          captionEn: image.caption_en || '',
          width: Number(image.width) || null,
          height: Number(image.height) || null,
          qualityNoteZh: image.quality_note_zh || '',
          source: image.source || null,
        })),
      }))
    )
    commit('setTermsLoadFailed', { atlas: false })
  },

  /**
   * 拉一次 KKASR 的 Key Info 詞表分類，取其中的 lesion 類別。
   *
   * 與圖鑑目錄同一個模式。拉不到的話語音編輯只填得了位置與大小（那兩個不靠
   * 這份清單），病灶名留空——降級，不是壞掉，但畫面要講出來。
   */
  async fetchLesionTerms({ state: s, commit, dispatch }) {
    const api = new KkasrApi(s.httpUrl)
    // 同 fetchAtlasCatalog：認得出自己是不是上一個世代發出的
    const epoch = storeEpoch
    const terms = await fetchWithRetry(async () => {
      const { ok, body } = await api.listKeyInfoTerms()
      if (!ok || !body || !body.categories) throw new Error('Key Info 詞表回應格式不符')
      return body.categories
    }, 'Key Info 詞表')

    if (epoch !== storeEpoch) return

    if (!terms) {
      commit('setTermsLoadFailed', { lesion: true })
      ensureTermsRetryTimer(dispatch)
      return
    }
    commit('setKeyInfoCategories', terms)
    commit('setTermsLoadFailed', { lesion: false })
  },

  /**
   * 手動收起圖鑑（面板上的關閉鈕）。與語音的 atlas_clear 等價。
   *
   * ⚠️ 光把畫面狀態清掉是不夠的：transport 仍可能重送最後一個 atlas_*，讓醫師
   *    關掉的圖鑑自己跳回來。所以連「關掉當下的最後一個 command seq」一起記下
   *    來；比這個 seq 舊的 atlas_* 一律不再顯示。
   *
   *    記的是**已經消費過**的最後一個 seq（`consumedCommandSeq`），不是
   *    `s.commands` 的最大 seq。兩者在 `consumeCommands` 的 await 期間會分岔：
   *    整批指令早就被 appendCommands 塞進 `s.commands` 了，但還沒輪到處理。
   *    取「已 append 的最大值」會把**醫師還沒看到**的那個 `show kudo` 一起關掉，
   *    而且是**永久**的（重送時同樣被抑制，後續正確事件也救不回來）。
   *
   *    「已消費」才是對的那條線：× 關的是醫師**當下看得到**的東西。還沒被消費
   *    的指令連畫面都還沒改過，本來就不在這次關閉的意思裡。
   *
   *    ⚠️ 不能改用 `commandCursor`——它在 appendCommands 就整批推進了，與
   *       `s.commands` 的最大 seq 是同一個時間點，一樣早。
   */
  clearAtlas({ state: s, commit }) {
    commit('setAtlasCommand', null)

    const clearedSeq = Math.max(Number(s.atlasClearedSeq) || 0, Number(s.consumedCommandSeq) || 0)
    commit('setAtlasClearedSeq', clearedSeq)
    saveSessionOverrides(s.sessionId, persistedOverrides(s))
  },

  /**
   * 卡片上的「結束編輯」鈕：滑鼠版的 photo over。
   *
   * 圖鑑有 × 鈕、finish 有手動按鈕，唯獨結束編輯以前只有語音一途——ASR 沒聽到
   * 那句話時醫師完全沒有補救手段，只能看著後面每一句繼續蓋進同一張卡片。
   *
   * @param {number} atSec 相對 session 起點的秒數（由元件用 startedAtMs 換算）
   */
  closePhotoEditing({ state: s, commit }, { atSec }) {
    const at = Number(atSec)
    if (!Number.isFinite(at) || at < 0) return
    commit('addManualPhotoOver', at)
    saveSessionOverrides(s.sessionId, persistedOverrides(s))
  },

  /** 截圖成功後才建立一段等效 photo N；失敗時不會留下假的編輯區間。 */
  openPhotoEditing({ state: s, commit }, { imgSerial, atSec }) {
    const param = Number(imgSerial)
    const at = Number(atSec)
    if (!(param > 0) || !Number.isFinite(at) || at < 0) return
    commit('addManualPhotoSelect', { param, atSec: at })
    saveSessionOverrides(s.sessionId, persistedOverrides(s))
  },

  /** 只有收到有效 server P 才出隊；identity 不屬於目前場次時 fail closed。 */
  ackCaptureRequest({ state: s, commit }, identity) {
    const sessionId = String(identity && identity.sessionId || '')
    const examinationId = String(identity && identity.examinationId || '')
    const seq = Number(identity && identity.seq)
    if (
      !Number.isFinite(seq)
      || String(s.sessionId || '') !== sessionId
      || String(s.examinationId || '') !== examinationId
    ) return false
    const pending = s.captureRequests.some((item) => (
      Number(item.seq) === seq
      && String(item.captureSessionId || '') === sessionId
      && String(item.captureExaminationId || '') === examinationId
    ))
    if (!pending) return false
    commit('dequeueCaptureRequest', { sessionId, examinationId, seq })
    saveSessionOverrides(s.sessionId, persistedOverrides(s))
    return true
  },

  startTranscriptPolling({ dispatch }) {
    if (transcriptTimer) return
    dispatch('pollDraftOnce')
    dispatch('pollOnce')
    draftTimer = window.setInterval(() => dispatch('pollDraftOnce'), POLL_DRAFT_MS)
    transcriptTimer = window.setInterval(() => dispatch('pollOnce'), POLL_TRANSCRIPT_MS)
  },

  /** F5 接回已 EOS session：只補 committed REFINE 與 LLM 狀態，FAST 不再復活。 */
  async startFinalizingPolling({ state: s, commit, dispatch }) {
    const epoch = storeEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    const isStale = () => (
      epoch !== storeEpoch || s.sessionId !== sessionId || s.examinationId !== examinationId
    )
    if (!transcriptTimer) {
      dispatch('pollOnce')
      transcriptTimer = window.setInterval(() => dispatch('pollOnce'), POLL_TRANSCRIPT_MS)
    }
    dispatch('stopTranscriptPollingAfterGrace')
    const api = new KkasrApi(s.httpUrl)
    try {
      const finalResponse = await api.finalTranscriptLatest(sessionId, examinationId)
      if (isStale()) return
      if (isAuthHttpResponse(finalResponse)) {
        return dispatch('handleFinalAuthRequired', { sessionId, examinationId })
      }
      if (
        finalResponse.ok
        && finalResponse.body
        && finalResponse.body.status === 'ai_final_unreviewed'
        && String(finalResponse.body.session_id || '') === String(sessionId)
      ) {
        commit('setFinalTranscript', finalResponse.body)
      }
    } catch (err) {
      // EOS 已成功，不把「畫面第一次抓 final 失敗」誤報成收尾失敗；LLM poll 會補抓。
      console.warn('[asrSession] final transcript first fetch failed', err)
    }
    if (!isStale()) {
      dispatch('startLlmPolling')
      return { ok: true }
    }
    return { ok: false, stale: true }
  },

  stopTranscriptPolling() {
    draftTimer = clearInterval_(draftTimer)
    transcriptTimer = clearInterval_(transcriptTimer)
    graceTimer = clearTimeout_(graceTimer)
  },

  /** EOS 之後 ASR 還會吐最後幾句，寬限期滿才真的停 */
  stopTranscriptPollingAfterGrace({ dispatch }) {
    graceTimer = clearTimeout_(graceTimer)
    graceTimer = window.setTimeout(() => {
      dispatch('stopTranscriptPolling')
    }, EOS_GRACE_MS)
  },

  /**
   * 結束收音，觸發 LLM。
   *
   * 結果不必在這裡等：KKASR 會把 report / extract 各推一次 webhook 回
   * kingcolon server 落地 DB，醫師直接去報告工作站看即可。這裡輪詢兩支
   * latest 端點只是為了把狀態列從「結束中」推到「報告已產生」，內容不取用。
   */
  async signalEos({ state: s, commit, dispatch }, audioTarget = null) {
    if (!s.sessionId) return { ok: false }
    const epoch = storeEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    const isStale = () => (
      epoch !== storeEpoch || s.sessionId !== sessionId || s.examinationId !== examinationId
    )
    commit('setStatus', 'finalizing')
    commit('setError', null)

    const api = new KkasrApi(s.httpUrl)
    try {
      const { ok, status, body } = await api.signalEos(sessionId, audioTarget)
      if (isStale()) return { ok: false, stale: true }
      if (
        !ok
        || !body
        || body.eos_accepted !== true
        || String(body.session_id || '') !== String(sessionId)
      ) {
        const message = (body && body.message) || `EOS 失敗 HTTP ${status}`
        commit('setStatus', 'error')
        commit('setError', message)
        return { ok: false, error: message }
      }
    } catch (err) {
      if (isStale()) return { ok: false, stale: true }
      const message = 'EOS 送出失敗（KKASR 連線中斷）'
      commit('setStatus', 'error')
      commit('setError', message)
      return { ok: false, error: message }
    }

    dispatch('stopTranscriptPollingAfterGrace')
    if (isStale()) return { ok: false, stale: true }
    // EOS receipt 已足夠讓檢查頁帶 exact session 進 Report2；final/report/extract
    // 交給既有只讀 poller。同步等第一次 final GET 會把醫師留在檢查頁，
    // 也讓 Report2 無法完整呈現 ASR finalizing 階段。
    dispatch('startLlmPolling')
    return { ok: true, eosAccepted: true, sessionId }
  },

  /**
   * 使用者中止收音（未 EOS）。KKASR 會捨棄整個 session、不跑 LLM。
   * 這一場的逐字稿不會產生報告——畫面必須講清楚，不能讓醫師以為存好了。
   */
  async abortSession({ state: s, commit, dispatch }, { sessionId = null } = {}) {
    const epoch = storeEpoch
    const targetSessionId = String(sessionId || s.sessionId || '')
    const targetExaminationId = s.examinationId == null ? null : String(s.examinationId)
    const isCurrent = () => (
      epoch === storeEpoch
      && String(s.sessionId || '') === targetSessionId
      && (s.examinationId == null ? null : String(s.examinationId)) === targetExaminationId
    )
    const fail = (error) => {
      if (isCurrent()) {
        commit('setStatus', 'error')
        commit('setError', error)
      }
      return { ok: false, error }
    }
    if (!targetSessionId || String(s.sessionId || '') !== targetSessionId) {
      return { ok: false, stale: true, error: 'ASR session 已切換，未執行中止' }
    }
    dispatch('stopTranscriptPolling')
    dispatch('stopLlmPolling')
    const api = new KkasrApi(s.httpUrl)
    let response
    try {
      response = await api.streamingOff(targetSessionId)
    } catch (err) {
      console.warn('[asrSession] streaming_off failed', err)
      return fail('ASR session 中止失敗（KKASR 連線中斷）')
    }

    const body = response && response.body
    if (
      !response
      || !response.ok
      || !body
      || body.status !== 'aborted'
      || String(body.session_id || '') !== targetSessionId
      || body.eos_accepted === true
    ) {
      return fail(
        (body && body.message)
        || `ASR session 中止失敗 HTTP ${(response && response.status) || 0}`
      )
    }

    if (!isCurrent()) {
      return { ok: false, stale: true, error: 'ASR session 已切換，中止結果未套用' }
    }

    commit('setStatus', 'aborted')
    return { ok: true, sessionId: targetSessionId }
  },

  startLlmPolling({ dispatch }) {
    if (llmTimer) return
    dispatch('pollLlmOnce')
    llmTimer = window.setInterval(() => dispatch('pollLlmOnce'), POLL_LLM_MS)
  },

  stopLlmPolling() {
    llmTimer = clearInterval_(llmTimer)
  },

  /**
   * ⚠️ 與 pollOnce 同樣**不可重入**，而且理由不只是「請求會累積」。
   *
   *    signalEos 會同時做兩件事：`stopTranscriptPollingAfterGrace`（逐字稿輪詢
   *    再跑 EOS_GRACE_MS = 60 秒）與 `startLlmPolling`。這 60 秒內兩條輪詢並存、
   *    打同一個 origin，而瀏覽器對同一 host 的併發連線有上限（HTTP/1.1 是 6）。
   *    LLM 這條若每 2 秒發一個沒回應的請求，約 12 秒就把連線佔滿——之後
   *    `/refine_result` 與 `/commands` 即使有 10 秒逾時，也只是在瀏覽器的佇列裡
   *    等到逾時被 abort。**EOS 之後才吐出來的那幾句 refine（寬限期存在的唯一
   *    理由）會因此收不到。**
   */
  async pollLlmOnce({ state: s, commit, dispatch }) {
    if (!s.sessionId) return
    if (llmPollInFlight) return
    const epoch = storeEpoch
    const sessionId = s.sessionId
    const examinationId = s.examinationId
    const isStale = () =>
      epoch !== storeEpoch
      || s.sessionId !== sessionId
      || s.examinationId !== examinationId
    const api = new KkasrApi(s.httpUrl)

    // 旗標緊貼 try，理由同 pollOnce
    llmPollInFlight = true
    try {
      if (!s.finalTranscript) {
        try {
          const finalResponse = await api.finalTranscriptLatest(sessionId, examinationId)
          if (isStale()) return
          if (isAuthHttpResponse(finalResponse)) {
            return dispatch('handleFinalAuthRequired', { sessionId, examinationId })
          }
          const { ok, body } = finalResponse
          if (
            ok
            && body
            && body.status === 'ai_final_unreviewed'
            && String(body.session_id || '') === String(sessionId)
          ) {
            commit('setFinalTranscript', body)
            commit('startReportWait', Date.now())
          } else if (
            body
            && body.status === 'ai_final_failed'
            && String(body.session_id || '') === String(sessionId)
          ) {
            const message = body.failure_category || '完整音訊定稿失敗'
            commit('setLlmFailure', { kind: 'final', error: message })
            commit('setError', message)
            commit('setStatus', 'error')
            dispatch('stopLlmPolling')
            return
          }
        } catch (err) {
          console.warn('[asrSession] final transcript poll failed', err)
        }
      }

      const finalReady = (
        s.finalTranscript
        && s.finalTranscript.status === 'ai_final_unreviewed'
        && String(s.finalTranscript.session_id || '') === String(sessionId)
      )
      if (finalReady) commit('startReportWait', Date.now())

      // 只看「有沒有跑完」，內容不取用——欄位抽取的正本由 webhook 落地
      // examination_transcripts，報告工作站從 DB 讀（見 evas2-report route）。
      try {
        const response = await api.llmExtractLatest(sessionId)
        if (isStale()) return
        const result = classifyLlmReadback(response, sessionId)
        if (result.status === 'auth') {
          return dispatch('handleFinalAuthRequired', { sessionId, examinationId })
        }
        if (result.status === 'succeeded') {
          commit('setExtractReady', true)
        } else if (result.status === 'failed') {
          const message = result.body.error || 'LLM 欄位擷取失敗'
          commit('setLlmFailure', { kind: 'extract', error: message })
          commit('setError', message)
          commit('setStatus', 'error')
          dispatch('stopLlmPolling')
          return
        } else if (result.status === 'invalid') {
          const message = `本場欄位擷取狀態無法核對（HTTP ${result.httpStatus}）`
          commit('setLlmFailure', { kind: 'extract', error: message })
          commit('setError', message)
          commit('setStatus', 'error')
          dispatch('stopLlmPolling')
          return
        }
      } catch (err) {
        console.warn('[asrSession] extract poll failed', err)
      }

      try {
        const response = await api.llmReportLatest(sessionId)
        if (isStale()) return
        const result = classifyLlmReadback(response, sessionId)
        if (result.status === 'auth') {
          return dispatch('handleFinalAuthRequired', { sessionId, examinationId })
        }
        if (result.status === 'succeeded') {
          commit('setReportReady', true)
        } else if (result.status === 'failed') {
          const message = result.body.error || 'LLM 報告產生失敗'
          if (result.body.delivery_status === 'failed') {
            const deliveryMessage = 'LLM 報告結果未能送達正式報告'
            commit('setLlmFailure', {
              kind: 'report_delivery',
              error: deliveryMessage,
              rawReportError: message,
            })
            commit('setError', deliveryMessage)
            commit('setStatus', 'error')
            dispatch('stopLlmPolling')
            return
          }
          // raw report worker 的失敗仍保留供診斷；canonical extract 可能稍後形成
          // 合法的 needs-review presentation，因此只有 delivery 已明確失敗才在這拍
          // 終止；succeeded／pending／not_required 仍交 canonical readback 收斂。
          commit('setLlmFailure', { kind: 'report_raw', error: message, aggregatePending: true })
        } else if (result.status === 'invalid') {
          const message = `本場報告狀態無法核對（HTTP ${result.httpStatus}）`
          commit('setLlmFailure', { kind: 'report', error: message })
          commit('setError', message)
          commit('setStatus', 'error')
          dispatch('stopLlmPolling')
          return
        }
      } catch (err) {
        console.warn('[asrSession] report poll failed', err)
      }

      let aggregate = s.reportAggregate
      try {
        const body = await evas2ReportServices.fetch(examinationId, { sessionId })
        if (isStale()) return
        const identityVerified = body && body.found === true
          ? String(body.kkasrSessionId || '') === String(sessionId)
          : String(body && body.requestedSessionId || '') === String(sessionId)
        aggregate = classifyEvas2ReportAggregate(body, { identityVerified })
        commit('setReportAggregate', aggregate)
        if (identityVerified && body && body.found === true) {
          commit('startReportWait', Date.now())
        }
      } catch (err) {
        if (isStale()) return
        if ([401, 403].includes(Number(err && err.status))) {
          return dispatch('handleFinalAuthRequired', { sessionId, examinationId })
        }
        if (!isRetryableRequestError(err)) {
          const message = `本場正式報告狀態無法核對（HTTP ${Number(err && err.status) || 0}）`
          commit('setReportAggregate', { status: 'failed', reason: 'request_invalid' })
          commit('setLlmFailure', { kind: 'aggregate', error: message })
          commit('setError', message)
          commit('setStatus', 'error')
          dispatch('stopLlmPolling')
          return
        }
        console.warn('[asrSession] canonical report poll failed', err)
      }

      if (aggregate.status === EVAS2_REPORT_AGGREGATE_STATUS.FAILED) {
        const message = aggregate.reason === 'extract_failed'
          ? 'LLM 欄位擷取失敗'
          : '本場正式報告狀態不完整或無法核對'
        commit('setLlmFailure', { kind: 'aggregate', error: message, reason: aggregate.reason })
        commit('setError', message)
        commit('setStatus', 'error')
        dispatch('stopLlmPolling')
        return
      }

      if (
        aggregate.status === EVAS2_REPORT_AGGREGATE_STATUS.PENDING
        && s.reportWaitStartedAtMs != null
        && Date.now() - s.reportWaitStartedAtMs >= EVAS2_REPORT_WAIT_TIMEOUT_MS
      ) {
        const rawFailure = s.llmFailure && s.llmFailure.kind === 'report_raw'
          ? s.llmFailure.error
          : ''
        const message = rawFailure
          ? `報告背景處理失敗，且正式報告等待已超過 4 分鐘：${rawFailure}`
          : '正式報告等待已超過 4 分鐘'
        commit('setReportAggregate', { status: 'failed', reason: 'timeout' })
        commit('setLlmFailure', { kind: 'aggregate_timeout', error: message, rawReportError: rawFailure })
        commit('setError', message)
        commit('setStatus', 'error')
        dispatch('stopLlmPolling')
        return
      }

      if (
        !isStale()
        && finalReady
        && [
          EVAS2_REPORT_AGGREGATE_STATUS.ACCEPTED,
          EVAS2_REPORT_AGGREGATE_STATUS.NEEDS_REVIEW,
        ].includes(aggregate.status)
      ) {
        commit('setReportReady', true)
        commit('setExtractReady', true)
        commit('setStatus', 'done')
        dispatch('stopLlmPolling')
      }
    } finally {
      llmPollInFlight = false
    }
  },

  /** 離開頁面時的總關閉：所有 timer 一律停掉，避免殭屍輪詢打 KKASR */
  teardown({ dispatch, commit }) {
    dispatch('stopTranscriptPolling')
    dispatch('stopLlmPolling')
    // 計時器不在 state 裡、resetSession 帶不到，而且沒有任何東西保證它會停——
    // 留著就是離開頁面後還在打 KKASR 的殭屍輪詢，一定要自己清。
    termsTimer = clearInterval_(termsTimer)

    // ⚠️ **五個 in-flight 旗標刻意不在這裡清**（`draftPollInFlight` / `pollInFlight`
    //    / `pacerFlushInFlight` / `termsRetryInFlight` / `llmPollInFlight`）。
    //
    //    它們每一個都是「緊貼 try 設定、由 finally 釋放」，不可能卡在 true，所以
    //    清它們解決不了任何問題；反過來卻會**打破 in-flight 保護**：某一支正在
    //    await 時 teardown 把旗標清成 false，下一次呼叫就進得來，兩個併行——
    //    對 pollOnce 來說那正是 P1-5 要消滅的重複行（實測會出現兩筆同 seq 的逐字稿）。
    //
    //    「離開頁面之後那些還在飛的請求怎麼辦」不是靠清旗標解決，是靠下面的世代
    //    比對：它們回來時會發現自己過期，什麼都不寫。
    // 推進世代：離開頁面**之前**發出、之後才回來的載入（最長 3 × 10 秒逾時 +
    // 1.5 秒退避 ≈ 31.5 秒）認得出自己過期了，不會在拆掉的 store 上重開計時器
    storeEpoch += 1
    commit('resetSession')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
